import { createPairKey } from '../utils/reactionUtils';

/**
 * Long write-ups for the famous reactions in chemistry/curatedReactions.js, one per curated pair,
 * shown on each reaction page under the result. Kept apart from the short `explanation` there,
 * which Fizzy reads aloud from recorded clips.
 *
 * Parts (headings come from utils/articleUtils.js): see, why, realLife, history (optional, only
 * well-documented facts), tryAtHome (a genuinely safe look-alike activity, or "Don't try this at
 * home" first for dangerous reactions) and a three-question quiz.
 */
export const REACTION_ARTICLES = Object.freeze([
  {
    pair: ['K', 'H2O'],
    see: [
      'Drop a tiny piece of potassium onto water and it does not sink. It floats, because potassium is lighter than water, and it starts to whizz around the surface straight away. Within a second or two it bursts into a lilac, pinky-purple flame. The little lump hisses, spins and often finishes with a sharp crack that throws hot drops into the air.',
      'When it is all over, the potassium has completely gone. The water looks just the same as before, but it has quietly changed. It is now a strong alkali that can burn skin.',
    ],
    why: [
      'Every atom has tiny particles called electrons whizzing around its outside. Potassium has just one electron in its outermost layer, and it is held very loosely. Potassium atoms give that electron away more easily than almost any metal you have heard of.',
      'Water molecules are happy to take it. When they do, each water molecule splits apart. Some of the hydrogen is let go as hydrogen gas, and the rest stays in the water as potassium hydroxide. Handing over electrons this easily gives out a big burst of heat, enough to melt the potassium into a ball and set the hydrogen gas alight. The lilac colour is the special colour potassium gives to a flame.',
    ],
    realLife: [
      'You will never see pure potassium outside a lab, because it reacts with water and even with damp air. Chemists keep it under oil. But potassium itself is everywhere in a safer form. Bananas, potatoes and beans are full of potassium compounds, and your heart and nerves need them to send their signals.',
      'Potassium hydroxide, the alkali made in this reaction, is used to make soft soaps, and it is the liquid inside many alkaline batteries.',
    ],
    history: [
      'Potassium was the first metal ever pulled out of a compound using electricity. In 1807 the English chemist Humphry Davy passed a strong current through slightly damp potash, a substance made from wood ash. Tiny shiny globules of metal appeared, and some burst into flame. His cousin, who worked with him, wrote that Davy danced around the room with delight. The name potassium comes from potash.',
    ],
    tryAtHome: [
      "Don't try this at home. Real potassium can explode, and the alkali it leaves behind burns skin and eyes.",
      'For a safe fizzing reaction, ask a grown-up for an effervescent vitamin tablet. Drop it into a glass of water and watch it skate and bubble as it releases gas. Count how many seconds it takes to disappear, then try again in warm water. Which is faster?',
    ],
    quiz: [
      {
        question: 'Why does potassium float on water?',
        answer: 'Because potassium metal is less dense than water, so it is lighter than the same amount of water.',
      },
      {
        question: 'What gas is given off when potassium reacts with water?',
        answer: 'Hydrogen gas, which catches fire with a lilac flame from the heat.',
      },
      {
        question: 'What did Humphry Davy use to get potassium out of potash in 1807?',
        answer: 'Electricity. He passed a strong electric current through potash.',
      },
    ],
  },
  {
    pair: ['Na', 'H2O'],
    see: [
      'A small piece of sodium dropped into a bowl of water fizzes loudly. It quickly melts into a shiny silver ball that races across the surface, pushed along by the gas it makes. You can hear it hissing. Sometimes it catches fire with a bright yellow-orange flame, and sometimes it pops at the very end.',
      'If a few drops of an indicator, a dye that changes colour in alkalis, are added to the water first, a pink trail follows the zooming ball. That trail shows where the new chemical has formed.',
    ],
    why: [
      'A sodium atom has one lonely electron on its outside. It is much happier without it, so when it meets water it hands that electron over. That breaks up the water molecules. Hydrogen gas is set free, and sodium hydroxide dissolves into the water.',
      "The reaction gives out enough heat to melt the sodium, which is why it turns into a ball. The hydrogen bubbles squirting out from underneath push the ball around like a tiny hovercraft. Sodium sits just above potassium in the periodic table. Its outer electron is held a bit more tightly, so the reaction is lively but usually a little calmer than potassium's.",
    ],
    realLife: [
      'Pure sodium is kept under oil in labs, but sodium compounds are all around you. Table salt is sodium chloride. Baking soda is sodium hydrogen carbonate. The yellow-orange glow of older street lamps came from hot sodium vapour.',
      'Sodium hydroxide, the substance made in this reaction, is one of the most used chemicals in the world. Factories use it to make soap, paper and drain cleaner.',
    ],
    history: [
      'Sodium was discovered just days after potassium. In 1807 Humphry Davy used electricity to split caustic soda and collected the new metal. That is where the name sodium comes from. Its symbol is Na because of natrium, an old name for soda.',
    ],
    tryAtHome: [
      "Don't try this at home. Sodium can catch fire on water, and the liquid left behind can burn skin.",
      'You can make something zoom across water safely. Cut a small boat shape from card with a notch at the back. Float it in a tray of water, then touch a drop of washing-up liquid into the notch. The soap pushes the boat forward, a bit like the gas pushes the sodium ball.',
    ],
    quiz: [
      {
        question: 'Why does the sodium melt into a ball?',
        answer: 'The reaction gives out enough heat to melt the sodium.',
      },
      {
        question: 'What makes the sodium ball race around on the water?',
        answer: 'Bubbles of hydrogen gas squirting out from underneath it push it along.',
      },
      {
        question: "Why is sodium's symbol Na?",
        answer: 'It comes from natrium, an old name linked to soda.',
      },
    ],
  },
  {
    pair: ['Li', 'H2O'],
    see: [
      'Lithium is the gentlest of the metals that react with water. A small piece floats, because lithium is the least dense metal of all, about half as heavy as the same amount of water. It fizzes steadily and slides slowly around the surface, getting smaller and smaller.',
      'Unlike sodium and potassium, lithium usually does not melt or catch fire. It just keeps bubbling calmly until it has all gone, leaving clear water that is now an alkali.',
    ],
    why: [
      'Lithium, sodium and potassium all live in the first column of the periodic table. They are called the alkali metals, and each one has a single electron in its outer layer that it wants to give away.',
      'In lithium, that outer electron is close to the centre of the atom, so it is held more tightly than in the bigger atoms below it. Lithium still gives its electron to water, breaking the water molecules into hydrogen gas and lithium hydroxide, but it does so more slowly. Less heat comes out at once, so the metal stays solid and the hydrogen does not usually light. Moving down the column from lithium to sodium to potassium, the reaction gets wilder each time.',
    ],
    realLife: [
      'Lithium is the star of rechargeable batteries. Phones, tablets, laptops and electric cars all run on lithium-ion batteries, because lithium is so light and gives up its electron so willingly.',
      'Lithium hydroxide, the substance made in this reaction, is used to make those batteries. It has also cleaned the air in spacecraft and submarines, because it soaks up the carbon dioxide that astronauts and sailors breathe out.',
    ],
    history: [
      'Lithium was discovered in 1817 by the Swedish chemist Johan August Arfwedson, who found it hiding inside a mineral called petalite. Because it was found in a rock, it was named after the Greek word lithos, which means stone.',
    ],
    tryAtHome: [
      "Don't try this with real lithium. It reacts with water and with damp air, and old batteries should never be opened.",
      'For a calm, steady fizz you can watch safely, drop a few raisins into a clear glass of fizzy water or lemonade. Gas bubbles stick to the raisins and lift them up. At the top the bubbles pop and the raisins sink again, dancing up and down for minutes.',
    ],
    quiz: [
      {
        question: 'Which alkali metal reacts most gently with water: lithium, sodium or potassium?',
        answer: 'Lithium. The reaction gets more violent going down the column to sodium and then potassium.',
      },
      {
        question: 'Why does lithium float?',
        answer: 'It is the least dense metal, only about half as dense as water.',
      },
      {
        question: 'Which everyday gadgets use lithium?',
        answer: 'Rechargeable batteries in phones, laptops and electric cars.',
      },
    ],
  },
  {
    pair: ['Cs', 'H2O'],
    see: [
      'Caesium is a soft, golden-coloured metal that melts at about 28 °C, just above room temperature, so it can turn liquid on a warm day. When even a tiny piece touches water there is no fizzing stage. It explodes instantly with a bang and a flash, and the blast can crack a glass container.',
      'The water left behind holds caesium hydroxide, one of the strongest alkalis there is.',
    ],
    why: [
      'Caesium sits near the bottom of the alkali metal column, far below lithium, sodium and potassium. Its atoms are huge, and the single outer electron is a long way from the centre of the atom. That electron is held so weakly that caesium gives it away faster than almost any other element.',
      'When caesium touches water, all the electron handing-over happens in a split second. Hydrogen gas and heat are produced so quickly that they cannot drift away gently. The sudden burst of hot gas is what makes the explosion. Caesium is also denser than water, so it sinks and reacts all over at once instead of floating on top.',
    ],
    realLife: [
      'Caesium is rare and is kept sealed in glass, but it does a very important job. The most accurate clocks in the world are caesium atomic clocks. Caesium atoms flip between two energy states at an incredibly steady rate, and since 1967 the official length of one second has been defined by counting these flips: just over nine billion of them.',
      'Your phone and GPS use time signals that come from atomic clocks like these.',
    ],
    history: [
      'Caesium was the first element discovered with a spectroscope, a tool that splits light into its colours. In 1860 Robert Bunsen and Gustav Kirchhoff heated drops of mineral water and saw two bright blue lines no one had seen before. They named the new element after the Latin word caesius, meaning sky blue.',
    ],
    tryAtHome: [
      "Don't try this at home. Caesium explodes on contact with water, and only a few specialist labs handle it.",
      "You can explore steady timekeeping safely. Tie a metal washer to a piece of string about 25 centimetres long and let it swing. Count the swings in 30 seconds, then try a longer string. Like caesium atoms in a clock, a pendulum's steady rhythm can measure time.",
    ],
    quiz: [
      {
        question: 'Why does caesium react even more violently than potassium?',
        answer: 'Its outer electron is much further from the centre of the atom, so it is given away even more easily and quickly.',
      },
      {
        question: 'What important job do caesium atoms do?',
        answer: 'They keep time in atomic clocks, which define the length of one second.',
      },
      {
        question: 'What does the name caesium mean?',
        answer: 'Sky blue, after the blue lines Bunsen and Kirchhoff saw in its light.',
      },
    ],
  },
  {
    pair: ['Ca', 'H2O'],
    see: [
      'Calcium metal is a dull silvery grey. Unlike sodium, it sinks to the bottom of the water. Then a steady stream of bubbles starts rising from it, and slowly the water turns cloudy and milky white.',
      'The reaction is lively but calm. The tube gets a little warm, but there is no flame or bang. After a while the calcium is gone and a white powder may settle at the bottom.',
    ],
    why: [
      'Calcium is in the second column of the periodic table, the alkaline earth metals. Each calcium atom has two electrons on its outside that it would like to give away. Giving away two is a little harder than giving away one, so calcium reacts more slowly than sodium or potassium.',
      'When the calcium hands its electrons to water, the water molecules break apart into hydrogen gas, which makes the bubbles, and calcium hydroxide. Calcium hydroxide only dissolves a tiny bit in water. Once the water holds as much as it can, the rest appears as tiny solid specks, and those specks scatter light, making the water look milky.',
    ],
    realLife: [
      'Calcium hydroxide is called slaked lime. Builders mix it with sand and water to make lime mortar and plaster, and it slowly hardens by soaking up carbon dioxide from the air. Gardeners spread lime on soil that is too acidic. Water companies use it to adjust drinking water.',
      'A clear solution of calcium hydroxide is called limewater. Scientists blow carbon dioxide through it as a test: if the limewater turns milky, the gas was carbon dioxide.',
    ],
    history: [
      'People have used lime for thousands of years. The ancient Romans heated limestone to make lime and used it in the mortar and concrete of buildings that still stand today. Calcium metal itself was first separated in 1808 by Humphry Davy, using electricity, just like potassium and sodium a year before.',
    ],
    tryAtHome: [
      'Real calcium metal should only be handled by trained scientists.',
      'Your eggshells, chalk and seashells are made of a calcium compound called calcium carbonate. With a grown-up, put a piece of eggshell or school chalk into a glass of vinegar. Bubbles of carbon dioxide will appear as the acid slowly dissolves it. Leave a whole raw egg in vinegar for two days and the shell disappears, leaving a bouncy egg in its skin. Wash your hands afterwards.',
    ],
    quiz: [
      {
        question: 'Why does the water turn milky?',
        answer: 'Calcium hydroxide does not dissolve well, so tiny solid specks form and scatter the light.',
      },
      {
        question: 'How many outer electrons does a calcium atom give away?',
        answer: 'Two, which is one reason it reacts more slowly than sodium.',
      },
      {
        question: 'What is limewater used to test for?',
        answer: 'Carbon dioxide. It turns milky when carbon dioxide bubbles through it.',
      },
    ],
  },
  {
    pair: ['HCl', 'Zn'],
    see: [
      'Drop a few pieces of zinc into dilute hydrochloric acid and tiny bubbles start forming on the metal straight away. Soon the whole tube is fizzing. The zinc slowly gets smaller as it dissolves, and the outside of the tube feels a little warm.',
      'If the gas is collected in an upside-down tube and a lit wooden splint is held to it, you hear a squeaky pop. That pop is the famous test that shows the gas is hydrogen.',
    ],
    why: [
      'An acid is a substance that contains hydrogen that can be given away easily. In hydrochloric acid, there are lots of hydrogen ions floating in the water. Each of them is a hydrogen atom that has lost its electron and badly wants one back.',
      'Zinc atoms have two outer electrons they are willing to give away. When zinc meets the acid, each zinc atom gives two electrons to two hydrogen ions. The hydrogen ions turn into hydrogen atoms, pair up and float away as bubbles of hydrogen gas. The zinc atoms, now missing two electrons, drift into the water and join with the chloride from the acid to make zinc chloride, which stays dissolved.',
    ],
    realLife: [
      'Zinc is used to protect steel. Steel coated in zinc is called galvanised steel, and you see it on buckets, fences and lamp posts. The zinc slowly reacts first, sacrificing itself so that the iron underneath does not rust.',
      'Zinc and acid reactions are also used in schools to make small amounts of hydrogen, and some batteries use zinc as the metal that gives away electrons.',
    ],
    history: [
      'In 1766 the English scientist Henry Cavendish collected the gas made when metals such as zinc dissolve in acids. He called it inflammable air because it burned so easily. Later, Antoine Lavoisier named it hydrogen, which means water maker. In 1783 Jacques Charles flew the first hydrogen balloon, filled with gas made by pouring acid onto metal.',
    ],
    tryAtHome: [
      "Don't use real hydrochloric acid at home. Acids burn skin and eyes.",
      'You can blow up a balloon with a safe kitchen reaction instead. With a grown-up, pour a little vinegar into a small bottle. Use a funnel to put two teaspoons of baking soda inside a balloon, stretch the balloon over the bottle neck, then lift it so the powder falls in. The gas made here is carbon dioxide, not hydrogen, so it will not float away or pop when lit.',
    ],
    quiz: [
      {
        question: 'What gas is made when zinc reacts with hydrochloric acid?',
        answer: 'Hydrogen gas.',
      },
      {
        question: 'What is the test for hydrogen?',
        answer: 'A lit splint held to the gas makes a squeaky pop.',
      },
      {
        question: 'Why is steel coated with zinc?',
        answer: 'The zinc reacts first and protects the iron underneath from rusting.',
      },
    ],
  },
  {
    pair: ['HCl', 'Mg'],
    see: [
      'A strip of magnesium ribbon dropped into dilute hydrochloric acid fizzes much more fiercely than zinc. It is covered in bubbles at once and may even rise to the top, carried by the gas. The tube gets noticeably warm, sometimes hot, and in a minute or two the ribbon has completely disappeared.',
      'The liquid left behind looks just like water, but it now holds dissolved magnesium chloride.',
    ],
    why: [
      'Like zinc, a magnesium atom gives away two outer electrons. But magnesium gives them away much more willingly, because it is higher in a list chemists call the reactivity series. The more reactive a metal is, the faster it pushes its electrons onto the hydrogen ions in the acid.',
      'Each hydrogen ion that gets an electron becomes a hydrogen atom, two atoms pair up, and the hydrogen gas bubbles away. The magnesium atoms drift into the water and pair with chloride. A lot of energy is released as heat, which is why the tube warms up. If you use thicker acid, or chop the magnesium into smaller bits, the bubbles come even faster, because more acid particles hit the metal every second.',
    ],
    realLife: [
      'Your stomach makes dilute hydrochloric acid to help digest food. Some indigestion medicines contain magnesium compounds, such as magnesium hydroxide, that react with extra stomach acid to calm heartburn.',
      'Magnesium itself is a very light metal. Mixed with aluminium, it makes strong but light parts for bikes, cars and laptops. Epsom salts, used in bath soaks, are a magnesium compound too.',
    ],
    history: [
      'Magnesium is named after Magnesia, a region of Greece where magnesium-rich minerals were found. In 1755 the Scottish chemist Joseph Black showed that magnesia was a different substance from lime, and in 1808 Humphry Davy made the first tiny sample of magnesium metal using electricity.',
    ],
    tryAtHome: [
      'Real acids and magnesium belong in a school lab with goggles.',
      'You can study what makes reactions faster at home. With a grown-up, take two tablets that fizz in water. Crush one into powder and leave the other whole. Drop each into its own glass of water at the same time. Which one finishes first? Smaller bits mean more surface for the water to reach.',
    ],
    quiz: [
      {
        question: 'Which reacts faster with acid, magnesium or zinc?',
        answer: 'Magnesium, because it is more reactive.',
      },
      {
        question: 'Why does the tube get warm?',
        answer: 'The reaction releases energy as heat.',
      },
      {
        question: 'Name one way to make the reaction go faster.',
        answer: 'Use stronger acid, warmer acid, or cut the magnesium into smaller pieces.',
      },
    ],
  },
  {
    pair: ['Cl', 'Na'],
    see: [
      'In a demonstration, a small piece of warm, melted sodium is lowered into a jar of pale green chlorine gas. The sodium bursts into a brilliant yellow flame. Thick white smoke swirls around the jar, and when it settles, a white powder coats the inside of the glass.',
      'That white powder is sodium chloride, ordinary table salt, made from two substances that are both dangerous on their own.',
    ],
    why: [
      'Sodium has one spare electron on its outside that it wants to lose. Chlorine is one electron short of a full outer layer and wants to gain one. Put them together and the electron jumps from sodium to chlorine.',
      'After the swap, the sodium has a positive charge and the chlorine has a negative charge. Opposite charges pull strongly on each other, so they stick together and stack up in a neat cube-shaped pattern called a crystal. This kind of bond, made by passing electrons from one atom to another, is called an ionic bond. Forming it releases a lot of energy, which is why there is a bright flame. The product behaves completely differently from what went in: no shiny metal, no poisonous gas, just salt.',
    ],
    realLife: [
      'Nobody makes table salt this way. We get it from sea water, salt lakes or underground salt mines. Salt flavours food, preserves fish and meat, and is spread on icy roads in winter so the ice melts.',
      'Factories also do the reverse. Passing electricity through salty water splits it to make chlorine, used to clean drinking water, and sodium hydroxide, used to make soap.',
    ],
    history: [
      'Chlorine was first made in 1774 by the Swedish chemist Carl Wilhelm Scheele. At the time people thought it was a compound. In 1810 Humphry Davy showed it was an element and named it from the Greek word chloros, meaning pale green.',
    ],
    tryAtHome: [
      "Don't try this at home. Chlorine gas is poisonous and sodium can catch fire.",
      'You can grow your own salt crystals safely. With a grown-up, stir salt into a cup of warm water until no more will dissolve. Pour a little onto a dark plate and leave it somewhere warm for a few days. As the water evaporates, small cube-shaped crystals appear. A magnifying glass shows their square sides.',
    ],
    quiz: [
      {
        question: 'What substance is made when sodium and chlorine react?',
        answer: 'Sodium chloride, which is table salt.',
      },
      {
        question: 'Which way does the electron move?',
        answer: 'From the sodium atom to the chlorine atom.',
      },
      {
        question: 'What shape are salt crystals?',
        answer: 'Cubes, because the ions stack in a cube-shaped pattern.',
      },
    ],
  },
  {
    pair: ['Mg', 'O'],
    see: [
      'Magnesium ribbon looks like a strip of dull silver tape. Hold it in a flame with tongs and for a moment nothing happens. Then it catches and burns with a dazzling white light, so bright that it can hurt your eyes. It crackles as it burns.',
      'When the light stops, the metal strip has turned into a crumbly white ash. That ash is magnesium oxide.',
    ],
    why: [
      'Magnesium atoms each have two outer electrons to give away, and oxygen atoms each want to gain two. They are a perfect match. Once the flame gives them enough energy to get started, magnesium and oxygen join together and release a huge amount of energy as heat and light.',
      'The heat from the first atoms reacting lights up the next ones, so the burning keeps going by itself along the ribbon. The light is so white because the burning metal gets extremely hot, and very hot things glow white. The new substance, magnesium oxide, is a solid where magnesium and oxygen ions are locked together tightly. It weighs more than the magnesium you started with, because oxygen from the air has joined on.',
    ],
    realLife: [
      'The bright white sparks in many fireworks and emergency flares come from burning magnesium or aluminium. Magnesium oxide is so hard to melt that it is used to line furnaces, and it is also used in some indigestion tablets.',
      'Burning magnesium is hard to put out. Water can make it worse, so firefighters use special dry powders.',
    ],
    history: [
      'In the 1860s photographers began burning magnesium ribbon and powder to light up dark rooms and caves. Later, flash powder made with magnesium went off with a bang and a cloud of white smoke. It was replaced by electric flashbulbs in the 20th century.',
    ],
    tryAtHome: [
      "Don't try this at home, and never stare at burning magnesium. Its light can damage your eyes.",
      'White light is really all the colours mixed together. Shine a torch onto the shiny side of an old CD or DVD, or put a glass of water on a sunny windowsill, and look for a small rainbow. The very bright light of burning magnesium contains all of these colours at once.',
    ],
    quiz: [
      {
        question: 'What colour light does burning magnesium give off?',
        answer: 'A dazzling, bright white light.',
      },
      {
        question: 'What is the white ash left behind?',
        answer: 'Magnesium oxide.',
      },
      {
        question: 'Why does the ash weigh more than the magnesium ribbon?',
        answer: 'Oxygen from the air has joined onto the magnesium.',
      },
    ],
  },
  {
    pair: ['Fe', 'O'],
    see: [
      'Rusting is a very slow reaction. Leave an iron nail in a damp place and after a day you may see small orange-brown spots. After a week it is covered in flaky, crumbly rust. The rust does not stick well, so it falls off and lets fresh iron underneath rust too.',
      'Iron kept completely dry, or completely away from air, hardly rusts at all.',
    ],
    why: [
      'Iron atoms are willing to give away electrons, and oxygen atoms want to take them. But iron and oxygen on their own react very slowly. Water speeds things up, because it helps the electrons travel from the iron to the oxygen. That is why iron rusts fastest when it is both wet and in contact with air.',
      'Salt dissolved in water makes rusting even faster, because salty water carries electric charges more easily. That is why cars rust more in places where salt is spread on winter roads, and why ships rust in the sea. Rust is a form of iron oxide that also has water locked inside it. It takes up more space than the iron did, which is why it flakes and cracks away.',
    ],
    realLife: [
      'Rust costs the world huge amounts of money every year, because it weakens bridges, cars, ships and pipes. People fight it with paint, oil and grease, plastic coatings, or a layer of zinc, which is called galvanising. Stainless steel has chromium added, which forms its own protective skin.',
      'Some hand warmers use rusting on purpose. They contain iron powder that rusts quickly when you open the packet, and the reaction gives out warmth for hours.',
    ],
    history: [
      'The Iron Pillar of Delhi in India is about 1,600 years old and has hardly rusted. Scientists have found that the iron contains a lot of phosphorus, which helped a thin protective layer form on its surface over the centuries.',
    ],
    tryAtHome: [
      'This one is safe to explore. Ask a grown-up for three iron nails or steel paper clips (not stainless steel) and three jars. Put one in an empty dry jar, one in tap water, and one in salty water. Check each day for a week and take photos. Which rusts first, and which hardly rusts at all? Wash your hands after touching rusty metal.',
    ],
    quiz: [
      {
        question: 'What two things does iron need to rust?',
        answer: 'Oxygen and water.',
      },
      {
        question: 'Why do cars rust faster where roads are salted?',
        answer: 'Salty water helps carry electric charges, which speeds up rusting.',
      },
      {
        question: 'Name one way to stop iron from rusting.',
        answer: 'Paint it, oil it, coat it in plastic, or coat it in zinc.',
      },
    ],
  },
  {
    pair: ['Cu', 'O'],
    see: [
      'Hold a shiny orange piece of copper in a hot flame with tongs and its surface quickly darkens. Rainbow colours shimmer across it for a moment, then it turns dull black. That black coating is copper oxide.',
      'Outdoors, copper changes in a different way. Over years, copper roofs, domes and statues turn from orange-brown to a soft blue-green.',
    ],
    why: [
      'Copper is not very reactive, so at room temperature it reacts with oxygen only very slowly. Heating gives the copper and oxygen atoms enough energy to react much faster. Each copper atom gives two electrons to an oxygen atom, and they lock together into black copper oxide. The rainbow colours you see at first come from a coating so thin that it reflects light like a soap bubble.',
      'The green colour outdoors is not just oxygen at work. Over many years, copper reacts with oxygen, water, carbon dioxide and small amounts of sulfur in the air. These make green copper compounds called a patina. The patina sticks well and protects the copper underneath, so copper roofs can last for over a hundred years.',
    ],
    realLife: [
      'Copper carries electricity very well, so most electrical wires inside walls and cables are made of copper. It is also used in water pipes, coins and saucepans.',
      'Many famous buildings have green copper roofs and domes. Some new buildings use copper that has been treated to look green straight away.',
    ],
    history: [
      'The Statue of Liberty in New York harbour has a skin of thin copper sheets. When it was finished in 1886 it was a shiny copper-brown. Over the next few decades it slowly turned green. People once debated painting it, but the patina was left because it protects the copper.',
    ],
    tryAtHome: [
      "Don't heat copper in a flame at home.",
      'You can grow green copper compounds safely. With a grown-up, fold a piece of kitchen paper, soak it in vinegar with a pinch of salt, and lay a copper coin or a piece of copper wire on it. Put it on a saucer and check it the next day. A blue-green crust may appear. Do not touch your mouth, wash your hands, and throw the paper away afterwards.',
    ],
    quiz: [
      {
        question: 'What colour does copper turn when heated in air?',
        answer: 'Black, because of a coating of copper oxide.',
      },
      {
        question: 'Why are old copper roofs green?',
        answer: 'Over years, copper reacts with oxygen, water, carbon dioxide and sulfur in the air to make a green patina.',
      },
      {
        question: 'Why is copper used for electrical wires?',
        answer: 'It carries electricity very well.',
      },
    ],
  },
  {
    pair: ['Al', 'O'],
    see: [
      'Aluminium looks like it does nothing at all with air. A can, a foil tray or a bike frame stays silvery for years. But the reaction has already happened. The moment fresh aluminium touches air, its surface reacts with oxygen and forms a layer of aluminium oxide so thin that you cannot see it.',
      'Scratch the metal and the shiny new surface is covered again in a fraction of a second.',
    ],
    why: [
      'Aluminium is actually a very reactive metal. Its atoms give away three electrons each, and oxygen is keen to take them. So when aluminium meets oxygen, a layer of aluminium oxide forms almost instantly.',
      'Unlike rust on iron, this layer is tough, sticks tightly and has no gaps. It is only a few millionths of a millimetre thick, yet it seals the metal underneath away from the air, so the reaction stops. Chemists call this kind of protection passivation. Aluminium oxide is also extremely hard. Rubies and sapphires are crystals of aluminium oxide coloured by tiny amounts of other elements.',
    ],
    realLife: [
      'Thanks to its invisible shield, aluminium is perfect for drink cans, window frames, aeroplanes and cooking foil. It is light, does not rust, and can be recycled again and again.',
      'Factories can make the oxide layer thicker on purpose, using electricity. This is called anodising. The thicker layer can soak up dyes, which is how coloured water bottles, laptops and carabiners get their bright metal colours.',
    ],
    history: [
      "Aluminium is the most common metal in Earth's crust, but it is locked tightly in rocks. In the mid-1800s it was so hard to extract that it cost more than silver. In 1884 a small aluminium pyramid was placed on top of the Washington Monument as a precious showpiece. Two years later, Charles Martin Hall in America and Paul Héroult in France, both in their early twenties, separately invented a way to make aluminium cheaply using electricity.",
    ],
    tryAtHome: [
      'This is safe to compare at home. With a grown-up, put a scrunched-up ball of aluminium foil and a steel paper clip (not stainless steel) in a glass of tap water. Leave them for a week. The paper clip will start to rust, while the foil stays mostly shiny, protected by its invisible oxide layer.',
    ],
    quiz: [
      {
        question: "Why doesn't aluminium rust away like iron?",
        answer: 'A thin, tough layer of aluminium oxide forms at once and seals the metal from the air.',
      },
      {
        question: 'Which gemstones are made of aluminium oxide?',
        answer: 'Rubies and sapphires.',
      },
      {
        question: 'What is anodising?',
        answer: 'Using electricity to make the oxide layer thicker, so it can be dyed bright colours.',
      },
    ],
  },
  {
    pair: ['Fe', 'S'],
    see: [
      'Start with grey iron filings and bright yellow sulfur powder. Stirred together, they make a speckled grey-yellow mixture, and a magnet can still pull the iron filings back out. Now heat one end of the mixture in a test tube. Soon it starts to glow red, and the glow creeps through the whole tube by itself, even after the flame is taken away.',
      'When it cools, there is a black, brittle lump. It is not grey, not yellow, and a magnet no longer picks it up the way it picked up the iron.',
    ],
    why: [
      'Before heating, the iron and sulfur are just a mixture: two substances sitting side by side, each keeping its own properties. That is why the magnet can separate them.',
      'Heat gives the atoms enough energy to start reacting. Iron atoms hand two electrons each to sulfur atoms, and the two lock together into a brand-new compound, iron sulfide. Making these new bonds gives out heat, and that heat starts the reaction in the next bit of mixture, so the glow travels along on its own. Iron sulfide has new properties: a new colour, it crumbles instead of bending, and it is not attracted to a magnet like iron is. This experiment is a famous way to show the difference between a mixture and a compound.',
    ],
    realLife: [
      "Iron and sulfur often join up in nature. The shiny gold-coloured mineral pyrite, nicknamed fool's gold, is made of iron and sulfur. Many miners in gold rushes were disappointed to find their treasure was pyrite.",
      'Iron sulfides form in mud at the bottom of ponds and the sea, where they can make the mud look black and smell a little of rotten eggs.',
    ],
    history: [
      'Long before matches, people made fire by striking flint against pyrite to make sparks. Some early guns from the 1500s, called wheellock guns, used a spinning steel wheel rubbing on a piece of pyrite to shower sparks into the gunpowder.',
    ],
    tryAtHome: [
      "Don't heat chemicals at home. When iron sulfide meets acid it can give off a smelly, poisonous gas.",
      'You can find real iron in your breakfast instead. Ask a grown-up for a breakfast cereal that says it contains iron. Crush a bowlful into powder in a zip-lock bag, add warm water, and leave it for 20 minutes. Then swirl a strong magnet against the outside of the bag for a minute. Tiny black specks of iron gather at the magnet.',
    ],
    quiz: [
      {
        question: 'How can you separate iron from sulfur before they are heated?',
        answer: 'Use a magnet to pull out the iron filings.',
      },
      {
        question: 'Why does the glow keep moving after the flame is removed?',
        answer: 'The reaction gives out heat, which starts the reaction in the next part of the mixture.',
      },
      {
        question: "What is fool's gold?",
        answer: 'Pyrite, a shiny mineral made of iron and sulfur.',
      },
    ],
  },
  {
    pair: ['S', 'Zn'],
    see: [
      'Zinc powder is grey and sulfur powder is yellow. Mixed together on a heatproof mat, they sit quietly, and nothing happens for as long as you like. But when a teacher lights the pile from a safe distance, it goes up all at once with a brilliant blue-white flash, a loud whoosh and a huge cloud of yellowish-white smoke.',
      'What is left is a pale, crusty solid called zinc sulfide.',
    ],
    why: [
      'Zinc atoms are keen to give away two electrons, and sulfur atoms are keen to take two. So zinc and sulfur are a good match, but at room temperature the atoms do not have enough energy to get the swap started. Chemists call this starting push the activation energy.',
      'A flame provides that push. Once the first atoms react, they release a lot of energy very quickly, which pushes the atoms next to them over the starting line too. The whole pile reacts in a second or two. The heat is so great that some of the zinc and sulfur boil and burn in the air, which is why there is so much smoke.',
    ],
    realLife: [
      'Zinc sulfide has an amazing trick. When small amounts of copper or silver are added, it stores up energy from light and then slowly gives it out as a glow. This made it a classic glow-in-the-dark material for watch dials, toys and stickers.',
      'Zinc sulfide also glows when it is hit by electrons or tiny particles, so it was used to coat the inside of old box-shaped television screens. The mineral sphalerite, which is mostly zinc sulfide, is the main ore that zinc metal is made from.',
    ],
    history: [
      "In 1909, in Ernest Rutherford's laboratory, Hans Geiger and Ernest Marsden fired tiny particles at thin gold foil and watched for flashes of light on a screen coated with zinc sulfide. Counting those flashes in a dark room helped them discover that every atom has a tiny, heavy nucleus at its centre.",
    ],
    tryAtHome: [
      "Don't try this at home. The flash is fierce and the smoke is harmful to breathe.",
      'You can play with glowing materials safely. Hold a glow-in-the-dark sticker or toy under a bright lamp for a minute, then take it into a dark room. Watch the glow fade. Try charging it for 10 seconds, then for 2 minutes. Does a longer charge make it glow longer?',
    ],
    quiz: [
      {
        question: "Why doesn't the zinc and sulfur mixture react until it is lit?",
        answer: 'The atoms need a push of energy, called activation energy, to get the reaction started.',
      },
      {
        question: 'What can zinc sulfide do when a little copper is added?',
        answer: 'It can glow in the dark after light shines on it.',
      },
      {
        question: 'What did Geiger and Marsden use zinc sulfide for?',
        answer: 'A screen that flashed when tiny particles hit it, so they could count them.',
      },
    ],
  },
  {
    pair: ['Al', 'Br'],
    see: [
      'Bromine is a dark red-brown liquid that gives off orange-brown fumes. When a piece of aluminium foil is dropped into it, at first nothing seems to happen. Then, after a few moments, the foil begins to sizzle, glows and throws out sparks and small flames, sometimes skating across the surface as it reacts.',
      'Thick fumes rise, and the aluminium is turned into aluminium bromide.',
    ],
    why: [
      'Each aluminium atom can give away three electrons, and each bromine atom wants to gain one. So one aluminium atom joins with three bromine atoms. Making those bonds releases a lot of energy, which is why the reaction gets so hot.',
      'Why the wait at the start? Aluminium is always covered in a thin, invisible layer of aluminium oxide that protects it. The bromine first has to work its way through that coat. Once fresh aluminium is exposed, the reaction heats itself up, which makes it go faster, which makes it hotter still. This kind of runaway speeding-up is what turns a quiet start into sparks.',
    ],
    realLife: [
      'You will not find aluminium bromide at home, but chemists use it as a helper, called a catalyst, to make other chemicals. Bromine itself turns up in many useful places. Bromine compounds help disinfect some hot tubs and spas. Others are added to plastics and fabrics to make them harder to set on fire.',
      'Old photographic film was coated with silver bromide, which changes when light hits it. That is how cameras captured pictures before digital sensors.',
    ],
    history: [
      'Bromine was discovered in 1826 by Antoine-Jérôme Balard, a young French chemist studying the salty water left over from salt marshes near Montpellier. He named it after the Greek word bromos, meaning stench, because of its strong, nasty smell.',
    ],
    tryAtHome: [
      "Don't try this at home. Bromine is poisonous and burns skin, and only trained chemists use it in a fume cupboard.",
      'To feel a reaction give out heat safely, ask a grown-up to help you mix a tablespoon of dried yeast with a little warm water, then pour it into a bottle holding half a cup of 3% hydrogen peroxide from a pharmacy and a squirt of washing-up liquid. Wear goggles. Foam rushes out, and if you touch the bottle it feels warm.',
    ],
    quiz: [
      {
        question: 'Why is there a delay before the aluminium starts sparking?',
        answer: 'The bromine must first get through the thin protective layer of aluminium oxide.',
      },
      {
        question: 'How many bromine atoms join with each aluminium atom?',
        answer: 'Three, because each aluminium atom gives away three electrons.',
      },
      {
        question: 'What does the name bromine mean?',
        answer: 'Stench, from a Greek word, because it smells very bad.',
      },
    ],
  },
  {
    pair: ['Al', 'I'],
    see: [
      'Aluminium powder and iodine crystals are mixed in a small heap. Nothing happens. Then a teacher, standing back in a fume cupboard, adds one single drop of water. After a few seconds the heap starts to smoke, then erupts in clouds of beautiful violet vapour, sometimes with sparks and flames.',
      'When it is over, aluminium iodide is left behind and purple stains may coat the glass of the fume cupboard.',
    ],
    why: [
      "Aluminium and iodine want to react: each aluminium atom can give three electrons to three iodine atoms. But in dry powders the particles barely touch, and the aluminium's thin oxide coat protects it.",
      "The drop of water acts as a starter. It dissolves a little iodine and helps it attack the aluminium's surface, so the first atoms react. That reaction gives out heat, which speeds up the rest, and soon the whole heap is extremely hot. The heat makes leftover iodine change straight from solid crystals into purple gas without melting first. This jump from solid to gas is called sublimation.",
    ],
    realLife: [
      'Iodine is an element your body needs in tiny amounts. Your thyroid gland uses it to make chemicals that control how fast your body works. That is why many countries add a little iodine to table salt, called iodised salt.',
      'Iodine solutions are also used to clean skin before an operation, because iodine kills germs. And seaweed, which soaks iodine out of sea water, is one of the richest natural sources.',
    ],
    history: [
      'Iodine was discovered by accident in 1811 by the French chemist Bernard Courtois. He was making saltpetre for gunpowder from seaweed ash and poured in some acid. Violet clouds rose up and settled as dark shiny crystals. The name comes from the Greek word for violet.',
    ],
    tryAtHome: [
      "Don't try this at home. Iodine fumes sting the eyes and lungs.",
      'Iodine has a famous colour test for starch. With a grown-up, put one drop of iodine antiseptic from a first-aid kit on a slice of raw potato, a piece of bread, and a slice of apple. Where there is starch, the brown drop turns dark blue-black. Do not eat the food afterwards, and be careful, as iodine stains.',
    ],
    quiz: [
      {
        question: 'What starts the aluminium and iodine reaction?',
        answer: 'A single drop of water.',
      },
      {
        question: 'What colour is iodine vapour?',
        answer: 'Violet, or purple.',
      },
      {
        question: 'Why is iodine added to table salt?',
        answer: 'Your thyroid gland needs iodine to make important body chemicals.',
      },
    ],
  },
  {
    pair: ['Cl', 'Sb'],
    see: [
      'In a famous demonstration, a pinch of grey, powdered antimony is sprinkled into a tall jar filled with greenish-yellow chlorine gas. The specks catch fire as they fall, making a shower of tiny glittering sparks, like a miniature firework inside the jar.',
      'White fumes form, and a white or pale yellow solid called antimony trichloride collects at the bottom.',
    ],
    why: [
      'Chlorine atoms are hungry for electrons. Antimony is a metalloid, a bit like a metal and a bit like a non-metal, and it will give up three electrons to make three bonds with chlorine.',
      'Because the antimony is powdered, every tiny speck has lots of surface touching the chlorine. The reaction starts by itself at room temperature and gives out so much heat that each speck glows white hot as it falls. That is why you see sparks rather than a flame. If there is plenty of chlorine, some antimony grabs five chlorine atoms instead of three, making a second compound.',
    ],
    realLife: [
      'Antimony is used to make lead harder, for example in the lead plates of car batteries. Antimony compounds help make fabrics, plastics and electronics harder to set on fire, and some are used to make glass clearer.',
      'In old chemistry books, antimony trichloride was called butter of antimony, because it is soft and greasy.',
    ],
    history: [
      "Antimony has been known for thousands of years. In ancient Egypt, a black powder made from the antimony mineral stibnite was one of the materials used in kohl, the dark eye makeup. That old name stibium is why antimony's chemical symbol is Sb.",
    ],
    tryAtHome: [
      "Don't try this at home. Chlorine is a poisonous gas and the fumes are harmful.",
      "You can see metal sparks safely with a grown-up. On a fireworks night, ask a grown-up to light a sparkler outside for you, wearing gloves and holding it at arm's length. The sparks are tiny bits of metal powder burning in the air, just like the antimony specks. Put used sparklers in a bucket of water.",
    ],
    quiz: [
      {
        question: 'Why does the antimony make sparks instead of one big flame?',
        answer: 'It is powdered, so each tiny speck reacts and glows white hot as it falls.',
      },
      {
        question: "Why is antimony's symbol Sb?",
        answer: 'It comes from stibium, an old name linked to the mineral stibnite.',
      },
      {
        question: 'What was antimony trichloride once called?',
        answer: 'Butter of antimony.',
      },
    ],
  },
  {
    pair: ['Li', 'N'],
    see: [
      'Freshly cut lithium is shiny and silvery. Left in the air, it slowly turns dull and then dark, and in a jar of pure nitrogen it gradually becomes coated with a reddish-purple to dark red solid. This is lithium nitride.',
      'There is no flash or fizz. It happens quietly, over minutes and hours.',
    ],
    why: [
      'Nitrogen gas is made of pairs of nitrogen atoms joined by a very strong triple bond. Breaking that bond takes a lot of energy, so nitrogen hardly reacts with anything at room temperature. That is why most metals can sit in air, which is about four-fifths nitrogen, without changing.',
      'Lithium is special. Its atoms are tiny and pack very tightly around nitrogen, so the energy released when lithium nitride forms is large enough to pay for breaking nitrogen apart. Three lithium atoms each give one electron to a single nitrogen atom. None of the other alkali metals, such as sodium or potassium, does this at room temperature.',
    ],
    realLife: [
      'Lithium nitride is studied by scientists because lithium can move through it easily, which could help make better batteries, and because it can soak up hydrogen.',
      "You meet nitrogen's calm nature every day. Bags of crisps are often filled with nitrogen instead of air, because nitrogen does not react with the food and keeps it fresh and uncrushed. Liquid nitrogen, which is extremely cold, is used to freeze food and in hospitals.",
    ],
    history: [
      'Nitrogen was discovered in 1772 by Daniel Rutherford, a Scottish medical student. He found that after a mouse had breathed the air in a sealed container, the leftover gas would not let candles burn. He called it noxious air.',
    ],
    tryAtHome: [
      "Don't handle real lithium. It reacts with damp air and water.",
      'Next time you open a packet of crisps, squeeze it first. That puffy cushion is mostly nitrogen, added in the factory to keep the crisps fresh. Compare a sealed packet with an opened one after a day. The opened crisps get soft because oxygen and moisture from the air got in.',
    ],
    quiz: [
      {
        question: 'Why does nitrogen hardly react with anything?',
        answer: 'Its two atoms are held together by a very strong triple bond that is hard to break.',
      },
      {
        question: 'Which is the only alkali metal that reacts with nitrogen at room temperature?',
        answer: 'Lithium.',
      },
      {
        question: 'Why are crisp packets filled with nitrogen?',
        answer: 'Nitrogen does not react with the crisps, so they stay fresh.',
      },
    ],
  },
  {
    pair: ['H', 'Pd'],
    see: [
      'If you put a piece of palladium, a shiny silvery metal, in a jar of hydrogen gas, you would not see anything dramatic. There is no flame or colour change. But if you could measure carefully, you would find the hydrogen in the jar disappearing, soaked up by the metal.',
      'The palladium swells very slightly and becomes a little more brittle. Heat it gently and the hydrogen comes back out.',
    ],
    why: [
      'Hydrogen atoms are the smallest atoms of all. Inside a piece of palladium, the metal atoms are stacked in a regular pattern with tiny gaps between them. At the palladium surface, hydrogen molecules split into single atoms, and those atoms can slip into the gaps, like marbles rolling into the spaces in a crate of oranges.',
      'This makes palladium hydride. It is not quite like an ordinary compound, because the amount of hydrogen inside can change, depending on the pressure and temperature. That is why palladium can let hydrogen in and then release it again.',
    ],
    realLife: [
      'Because only hydrogen can squeeze through palladium, thin palladium sheets are used as filters to make extremely pure hydrogen.',
      "Palladium is also a great catalyst, a helper that speeds up reactions without being used up. Most of the world's palladium goes into catalytic converters in car exhausts, which turn harmful gases into less harmful ones. It is used in electronics, dentistry and white-gold jewellery too.",
    ],
    history: [
      'Palladium was discovered by the English chemist William Hyde Wollaston in the early 1800s. He named it after Pallas, an asteroid found in 1802. Unusually, he first offered the new metal for sale anonymously in a London shop, and some chemists thought it was a fake. In 1866 Thomas Graham discovered that palladium could soak up hydrogen.',
    ],
    tryAtHome: [
      "You can't get palladium at home, but you can model how a solid holds something in its gaps. Weigh a dry kitchen sponge, then dip it in water and weigh it again. Squeeze it out over a measuring jug. How much water did it hold? Palladium holds hydrogen atoms in its gaps in a similar way, and gives them up when heated.",
    ],
    quiz: [
      {
        question: 'Why can hydrogen fit inside palladium?',
        answer: 'Hydrogen atoms are tiny and slip into the gaps between the palladium atoms.',
      },
      {
        question: 'How do you get the hydrogen back out?',
        answer: 'Heat the palladium gently.',
      },
      {
        question: 'What is palladium named after?',
        answer: 'Pallas, an asteroid discovered in 1802.',
      },
    ],
  },
  {
    pair: ['Hg', 'S'],
    see: [
      'Mercury is the only metal that is liquid at room temperature. It forms shiny, wobbling silver droplets. If yellow sulfur powder is rubbed into mercury, the droplets start to disappear and a black powder forms.',
      'Over time, or with gentle warming in the right conditions, black mercury sulfide can change into a red form, the famous bright red pigment called vermilion.',
    ],
    why: [
      'Mercury atoms are willing to share electrons with sulfur, and the bond they make is very strong. Grinding the two together keeps bringing fresh mercury surface into contact with sulfur, so the reaction can happen even without heating.',
      'Mercury sulfide is extremely insoluble, which means it hardly dissolves in water at all. That traps the mercury in a solid and stops it giving off the poisonous vapour that liquid mercury slowly releases. The same compound comes in black and red forms because its atoms can stack up in two different crystal patterns.',
    ],
    realLife: [
      'The natural red mineral cinnabar is mercury sulfide. It has been the main ore of mercury for thousands of years. Ground into powder, it made vermilion, a brilliant red paint.',
      'Mercury spill kits often contain sulfur powder. Sprinkling it on spilled mercury helps bind the droplets so they are safer to clean up. Today mercury is used much less than it used to be, and many countries are cutting its use because it is so harmful.',
    ],
    history: [
      'The mercury mines of Almadén in Spain were worked for around 2,000 years, until the early 2000s, and were among the largest sources of mercury in the world. In ancient China, cinnabar was used to colour the famous red lacquer on precious boxes and furniture, and Roman painters used vermilion on the walls of wealthy homes.',
    ],
    tryAtHome: [
      "Don't try this at home. Mercury and its compounds are poisonous, and old mercury thermometers should be given to a grown-up to dispose of safely.",
      "You can make paint the old-fashioned way instead. With a grown-up, mix an egg yolk with a teaspoon of water, then stir in coloured powders such as paprika, turmeric or crushed chalk. This egg paint is called tempera and was used by painters for centuries. Wash your hands and don't eat it.",
    ],
    quiz: [
      {
        question: 'What is the red mineral made of mercury and sulfur called?',
        answer: 'Cinnabar.',
      },
      {
        question: 'Why is sulfur used to clean up mercury spills?',
        answer: 'It reacts with mercury to make mercury sulfide, a solid that traps the mercury.',
      },
      {
        question: 'What red paint is made from mercury sulfide?',
        answer: 'Vermilion.',
      },
    ],
  },
  {
    pair: ['Ag', 'S'],
    see: [
      'Shiny silver spoons, jewellery and candlesticks slowly lose their sparkle. First they turn a faint yellow, then brown, and finally a dull black. This dark coating is called tarnish.',
      'Rub it with silver polish and the black comes off on the cloth, leaving bright silver again.',
    ],
    why: [
      'Silver does not react with the oxygen in air, which is one reason it was so prized. But it does react, very slowly, with sulfur. The air always contains tiny amounts of a sulfur gas called hydrogen sulfide, the gas that gives rotten eggs their smell. Foods such as eggs, onions and mustard, and things like rubber bands and wool, give off sulfur compounds too.',
      'Silver atoms on the surface join with the sulfur to make silver sulfide, which is black. The layer starts so thin that it just looks yellowish, then builds up over weeks and months. Polish works by rubbing the silver sulfide off, which also removes a tiny bit of silver each time.',
      'Tarnish is not dirt that can be wiped away with water. It is a new substance that has formed on the surface, so it needs a chemical or a polish to remove it.',
    ],
    realLife: [
      'People wrap silver in special cloths and keep it in airtight bags to slow tarnishing. Museums control the air around old silver for the same reason.',
      'Silver is the best conductor of electricity of all the metals. It is used in electrical contacts and some electronics, in mirrors, in solar panels, and in some bandages because silver kills germs.',
    ],
    history: [
      'The chemical symbol for silver, Ag, comes from argentum, the Latin word for silver. The country Argentina got its name from the same word, because early European explorers hoped to find silver there.',
    ],
    tryAtHome: [
      'This is a safe one to explore. With a grown-up, mash a hard-boiled egg yolk on a saucer and press a silver-coloured spoon into it. Leave it overnight. The next day, wash the spoon and look for dark marks. Egg yolk gives off sulfur that tarnishes real silver or silver plating.',
      'To clean it, a grown-up can line a bowl with foil, add a spoon of baking soda and hot water, and lay the spoon on the foil. The sulfur moves from the silver to the aluminium foil.',
    ],
    quiz: [
      {
        question: 'What is the black tarnish on silver?',
        answer: 'Silver sulfide.',
      },
      {
        question: 'Does silver react with the oxygen in air?',
        answer: 'No. It reacts slowly with sulfur instead.',
      },
      {
        question: 'Which country is named after silver?',
        answer: 'Argentina, from the Latin word argentum.',
      },
    ],
  },
  {
    pair: ['O', 'Os'],
    see: [
      'Osmium is a hard, bluish-silver metal. A solid lump looks like it does nothing in air. But fine osmium powder slowly reacts with the oxygen in air, and faster when it is heated, giving off a gas with a sharp, nasty smell a bit like chlorine.',
      'That gas is osmium tetroxide. It can cool into pale yellow crystals that turn back into vapour very easily.',
    ],
    why: [
      'Osmium can do something unusual: it can share up to eight of its electrons with other atoms. With oxygen, one osmium atom bonds to four oxygen atoms, making osmium tetroxide.',
      'The four oxygen atoms surround the osmium in a neat, balanced shape, and the molecules only hold onto each other weakly. That is why osmium tetroxide turns into a vapour so easily, even at room temperature. Powder reacts faster than a lump because powder has much more surface touching the air.',
    ],
    realLife: [
      'Osmium tetroxide is very poisonous and can damage eyes, so only experts use it. Scientists use tiny amounts to stain fats in cells so they show up dark under an electron microscope. It has also been used to reveal fingerprints.',
      'Osmium metal is incredibly hard and is the densest element found in nature. A football-sized ball of osmium would weigh more than a grown-up. Hard osmium alloys were once used for fountain pen tips and record player needles.',
      "Osmium is one of the rarest elements in Earth's crust. It is usually found mixed with platinum and iridium, and only small amounts are produced each year.",
    ],
    history: [
      'Osmium was discovered in 1803 by the English chemist Smithson Tennant, who found it in the black leftovers after dissolving platinum ore. He named it after osme, the Greek word for smell, because of the sharp odour of its oxide.',
    ],
    tryAtHome: [
      "Don't try this at home. Osmium tetroxide is very poisonous.",
      'You can study how smells travel safely. Ask a grown-up to open a bottle of vanilla essence or peel an orange in one corner of a room while you and a friend stand at different distances. Raise your hand the moment you smell it. The smell moves because its tiny molecules drift through the air, just like osmium tetroxide vapour.',
    ],
    quiz: [
      {
        question: 'How many oxygen atoms bond to one osmium atom in osmium tetroxide?',
        answer: 'Four.',
      },
      {
        question: 'Why does osmium powder react faster than a lump?',
        answer: 'Powder has much more surface touching the air.',
      },
      {
        question: 'What does the name osmium mean?',
        answer: 'Smell, from the Greek word osme.',
      },
    ],
  },
  {
    pair: ['F', 'U'],
    see: [
      'In a special nuclear facility, uranium reacts with fluorine gas to form uranium hexafluoride. At room temperature it is a white, crystally solid. Warm it to about 56 °C and it turns straight into a gas without melting first.',
      'There is nothing to see from outside. The work happens in sealed steel equipment behind thick walls.',
    ],
    why: [
      "Fluorine is the most electron-hungry element of all. It is so strong that it can pull six electrons' worth of bonding from a single uranium atom, so each uranium ends up bonded to six fluorine atoms.",
      'This compound is useful for a clever reason. Uranium atoms come in two main types, called isotopes, that are almost identical except that one is very slightly heavier. Fluorine atoms in nature are all exactly the same weight. So any difference in weight between two uranium hexafluoride molecules comes only from the uranium. Because it becomes a gas so easily, the molecules can be spun at enormous speeds in machines called centrifuges, where the slightly heavier ones drift outward and can be separated.',
    ],
    realLife: [
      'Separating uranium this way is called enrichment. Enriched uranium is used as fuel in nuclear power stations, which make electricity without burning coal or gas.',
      'Uranium hexafluoride is radioactive and also reacts with water to make dangerous acid, so it is moved in thick steel cylinders and handled with great care.',
    ],
    history: [
      'Uranium was discovered in 1789 by the German chemist Martin Heinrich Klaproth and named after the planet Uranus, found eight years earlier. In 1896 Henri Becquerel noticed that uranium salts fogged photographic plates in the dark, which led to the discovery of radioactivity.',
    ],
    tryAtHome: [
      "Don't try this at home. Uranium and fluorine are extremely dangerous and are only handled in special nuclear labs.",
      'You can try sorting by size and weight safely. Put a cup of uncooked rice and a ping-pong ball in a jar, bury the ball at the bottom, close the lid and shake the jar gently from side to side. The ball rises to the top. Shaking and spinning are clever ways to sort mixtures.',
    ],
    quiz: [
      {
        question: 'How many fluorine atoms bond to each uranium atom in uranium hexafluoride?',
        answer: 'Six.',
      },
      {
        question: 'Why is it useful that all natural fluorine atoms weigh the same?',
        answer: 'Any weight difference between molecules comes only from the uranium, so the uranium isotopes can be sorted.',
      },
      {
        question: 'What was uranium named after?',
        answer: 'The planet Uranus.',
      },
    ],
  },
  {
    pair: ['Cu', 'Zn'],
    see: [
      'Copper is orange-pink and zinc is bluish-grey. When the two metals are melted together in a furnace and the liquid is poured into a mould, it cools into a shiny golden-yellow metal. This is brass.',
      'The more zinc you add, the paler and more yellow the brass becomes.',
    ],
    why: [
      'Copper and zinc do not swap electrons or make a new compound. Instead, when they are both melted, their atoms mix together evenly, like salt dissolved in water. When the liquid cools, zinc atoms are left scattered among the copper atoms in the solid. A mixture of metals like this is called an alloy.',
      "The zinc atoms are a slightly different size from the copper atoms, so they upset the neat rows inside the metal. Rows that are bumpy cannot slide past each other so easily, which makes brass harder and stronger than pure copper. The zinc also changes the colour, turning copper's pink-orange into gold.",
    ],
    realLife: [
      'Brass is easy to shape and does not rust, so it is used for door handles, keys, zips, screws, plumbing taps and fittings. Trumpets, trombones and tubas are called brass instruments because they are made of it.',
      'Brass and other copper alloys also slowly kill germs that land on them, which is one reason brass door handles were common in busy buildings.',
    ],
    history: [
      'People made brass long before anyone could make pure zinc. The Romans heated copper with a zinc ore called calamine and charcoal, and the zinc soaked into the copper. They used brass for coins, called sestertii, and for armour decorations.',
      "Later, brass became so important for ships' instruments, clocks and cannons that whole towns grew up around brass workshops. Musicians still prize old brass instruments for their warm sound.",
    ],
    tryAtHome: [
      'Melting metal is only for experts, but you can go on a brass hunt. With a grown-up, look for golden-coloured metal around your home, such as keys, door handles, hinges or zips. Hold a fridge magnet to each one. Brass is not magnetic, so the magnet will not stick. Gold-coloured items that do stick are probably steel with a brass-coloured coating.',
    ],
    quiz: [
      {
        question: 'Is brass a compound or an alloy?',
        answer: 'An alloy, a mixture of metals.',
      },
      {
        question: 'Why is brass harder than pure copper?',
        answer: 'The different-sized zinc atoms make it harder for the rows of atoms to slide.',
      },
      {
        question: 'Is brass attracted to a magnet?',
        answer: 'No, brass is not magnetic.',
      },
    ],
  },
  {
    pair: ['Cu', 'Sn'],
    see: [
      'Melt orange copper with a smaller amount of silvery tin, stir, and pour the glowing liquid into a mould. As it cools it becomes a warm reddish-brown metal: bronze. A typical bronze is about nine parts copper to one part tin.',
      'Tap a bronze bowl and it rings with a clear, long note. Leave bronze outdoors for years and it slowly darkens to brown or turns green.',
    ],
    why: [
      'Like brass, bronze is an alloy, not a compound. The copper and tin atoms do not swap electrons. When melted, they mix evenly, and when the liquid freezes, tin atoms are trapped here and there among the copper atoms.',
      'Tin atoms are bigger than copper atoms. They jam the neat rows of copper atoms so the rows cannot slide past each other easily. That makes bronze much harder than either copper or tin alone, so a bronze blade keeps its edge. Bronze also melts at a lower temperature than pure copper and flows well into moulds, which made it ideal for casting tools, weapons and statues.',
    ],
    realLife: [
      'Bronze is used for bells, cymbals and statues, because it rings beautifully and lasts for centuries outdoors. It is also used for ship parts and bearings, because it resists salty sea water and slides smoothly against steel.',
      'Third-place medals are called bronze medals, although modern ones are usually made from copper mixed with zinc and a little tin.',
    ],
    history: [
      'Bronze changed the world. Around 5,000 years ago, people in the Middle East discovered that adding tin to copper made a much tougher metal. This began a period called the Bronze Age, when bronze was used for tools, weapons, armour and art. The Colossus of Rhodes, a giant statue of the sun god built around 280 BC, was covered in bronze plates.',
    ],
    tryAtHome: [
      'Melting metal is only for experts, but you can model why alloys are harder. Fill a tray with marbles of the same size in neat rows and tilt it: the rows slide easily. Now swap in a few bigger balls, such as ping-pong balls. Tilt it again and the rows jam. The big balls act like tin atoms in bronze.',
    ],
    quiz: [
      {
        question: 'Which two metals make bronze?',
        answer: 'Copper and tin.',
      },
      {
        question: 'Why is bronze harder than copper?',
        answer: 'The bigger tin atoms stop the rows of copper atoms sliding past each other.',
      },
      {
        question: 'What do we call the period when people first made lots of bronze tools?',
        answer: 'The Bronze Age.',
      },
    ],
  },
  {
    pair: ['C', 'Fe'],
    see: [
      'Steel is made by melting iron and controlling how much carbon is in it. Pure iron is fairly soft and bends easily. Add a small pinch of carbon, usually less than one part in a hundred, and the metal that cools from the furnace is far stronger and tougher.',
      'If a steel blade is heated red hot and then plunged into cold water, it hisses and becomes even harder.',
    ],
    why: [
      'Iron atoms are packed in neat layers that can slide over each other, which is why pure iron bends. Carbon atoms are much smaller than iron atoms. Instead of replacing iron atoms, they squeeze into the tiny gaps between them.',
      'These carbon atoms act like grit in the layers, making it much harder for them to slide. A little carbon gives strong, springy steel. Too much carbon makes the metal hard but brittle, so it can snap. Cooling steel suddenly, called quenching, traps the atoms in a strained arrangement that is even harder. Steelmakers also add other elements: chromium and nickel make stainless steel, which resists rust.',
      "Most of the world's iron ore is turned into steel, so this pinch of carbon matters more than almost any other recipe in industry.",
    ],
    realLife: [
      'Steel is everywhere: in bridges, skyscraper frames, cars, ships, trains and rails, tools, cutlery, saucepans and washing machines. Concrete in big buildings is often strengthened with steel bars.',
      'Steel is also one of the most recycled materials on Earth. Old cars and tins are melted down and made into new steel again and again.',
    ],
    history: [
      'People have made steel for thousands of years, but it was slow and expensive. In 1856 the English inventor Henry Bessemer found a way to blow air through melted iron to burn off extra carbon quickly. His process made steel cheap enough for railways, ships and tall buildings.',
    ],
    tryAtHome: [
      'Hot metal is only for experts, but you can test metals with a magnet. With a grown-up, hold a fridge magnet to a steel tin can, a steel spoon, a stainless steel spoon and a piece of aluminium foil. Most ordinary steel sticks. Some stainless steel spoons do not, because the nickel added to them changes how their atoms are arranged.',
    ],
    quiz: [
      {
        question: 'What element is added to iron to make steel?',
        answer: 'Carbon.',
      },
      {
        question: 'Why does a little carbon make iron stronger?',
        answer: 'The small carbon atoms sit in the gaps and stop the layers of iron atoms sliding.',
      },
      {
        question: 'What is added to make stainless steel?',
        answer: 'Chromium, often with nickel.',
      },
    ],
  },
  {
    pair: ['K', 'Na'],
    see: [
      'Sodium and potassium are both soft metals that are solid at room temperature. Press clean pieces of them together, under oil or in a dry gas so that air cannot reach them, and something surprising happens. Where they touch, they melt into a shiny silver liquid, even though the room is not hot.',
      'This liquid metal is called NaK, pronounced like the word knack.',
    ],
    why: [
      "Sodium and potassium do not react with each other. They form an alloy. On its own, sodium melts at about 98 °C and potassium at about 63 °C. But when their atoms are mixed, they get in each other's way. Neither kind of atom can line up into its own neat solid pattern, so the mixture stays liquid at a much lower temperature.",
      'The best mixture, with roughly three parts potassium to one part sodium, stays liquid down to about minus 12 °C, colder than a freezer. This effect, where a mixture melts at a lower temperature than either ingredient, is the same reason salt melts ice on a winter road.',
    ],
    realLife: [
      'NaK is excellent at carrying heat and stays liquid over a wide range of temperatures. It has been used as a coolant in some experimental nuclear reactors and in heat-transfer systems.',
      'But NaK is very dangerous. It can catch fire on its own in air and explodes with water, so it is only handled by experts in sealed equipment.',
    ],
    history: [
      'In 1951 an experimental reactor in Idaho, USA, called EBR-I became the first nuclear reactor to make usable electricity, lighting up four light bulbs. Its heat was carried away by liquid NaK.',
    ],
    tryAtHome: [
      "Don't try this at home. Sodium, potassium and NaK can catch fire or explode.",
      'You can use the same melting trick to make ice cream. With a grown-up, put half a cup of milk, a spoon of sugar and a drop of vanilla in a small zip-lock bag. Put it inside a big bag full of ice and six tablespoons of salt. Wear gloves and shake for ten minutes. The salt makes the ice melt at a lower temperature, so the mixture gets cold enough to freeze the milk.',
    ],
    quiz: [
      {
        question: 'What state is NaK at room temperature?',
        answer: 'Liquid.',
      },
      {
        question: 'Why does mixing sodium and potassium lower the melting point?',
        answer: "The different atoms get in each other's way, so neither can form its neat solid pattern.",
      },
      {
        question: 'What everyday use of salt works in a similar way?',
        answer: 'Salt on icy roads, which makes ice melt at a lower temperature.',
      },
    ],
  },
  {
    pair: ['Al', 'Ga'],
    see: [
      'Gallium is a silvery metal that melts at about 30 °C, so it turns liquid if you hold it in your hand for a while. Put a drop of liquid gallium on a scratched aluminium strip and wait. Over hours, the gallium disappears into the aluminium.',
      'The strip looks almost the same, but now it has become crumbly. You can snap it with your fingers, and it may tear like wet cardboard.',
    ],
    why: [
      'Aluminium is made of many tiny crystals called grains, packed tightly together. Normally its oxide skin keeps everything out. A scratch lets liquid gallium touch fresh aluminium.',
      'Gallium does not make a new compound. Instead, it seeps along the boundaries between the aluminium grains and coats them with a thin liquid layer. The grains are no longer stuck firmly together, so the metal falls apart under very little force. Scientists call this liquid metal embrittlement. Gallium also stops the aluminium from rebuilding its protective oxide layer, so the aluminium can corrode quickly in damp air.',
    ],
    realLife: [
      'Because liquid gallium can weaken aluminium, airlines do not allow gallium or mercury on planes. Many aircraft parts are made of aluminium alloys.',
      'Gallium itself is very useful. Gallium compounds are used to make LEDs, including the blue LEDs that make white LED lights possible, and the chips in phones and solar panels.',
    ],
    history: [
      "Gallium has a famous story. In 1871 the Russian chemist Dmitri Mendeleev used his periodic table to predict an unknown element he called eka-aluminium, and even guessed its weight and density. In 1875 the French chemist Paul-Émile Lecoq de Boisbaudran discovered gallium, and it matched Mendeleev's predictions very closely. It was named after Gallia, the Latin name for France.",
    ],
    tryAtHome: [
      'If your family has gallium from a science kit, let a grown-up help. Melt it in a plastic bag in your warm hand, and keep it away from anything made of aluminium. Wash your hands afterwards, as it leaves grey stains.',
      'No gallium? Compare melting points with food instead. Put a square of chocolate, a cube of butter and an ice cube on separate plates in a warm room. Which one melts first?',
    ],
    quiz: [
      {
        question: 'At about what temperature does gallium melt?',
        answer: 'About 30 °C, so it can melt in your hand.',
      },
      {
        question: 'What does gallium do to aluminium?',
        answer: 'It seeps between the aluminium grains and makes the metal weak and crumbly.',
      },
      {
        question: 'Who predicted gallium before it was discovered?',
        answer: 'Dmitri Mendeleev, using his periodic table.',
      },
    ],
  },
  {
    pair: ['Au', 'Hg'],
    see: [
      'Drop a tiny flake of gold into a puddle of liquid mercury and the gold seems to vanish. The bright yellow colour disappears and the mercury becomes a little thicker. Add more gold and you get a soft, silvery paste, like putty.',
      'Heat that paste strongly and the mercury boils away as an invisible, poisonous vapour, leaving a spongy lump of pure gold behind.',
    ],
    why: [
      'Mercury is a liquid metal, and many other metals can dissolve in it, a bit like sugar dissolving in tea. A metal dissolved in mercury is called an amalgam. Gold atoms mix in among the mercury atoms easily because the two metals get along well and can share their electrons in a metallic way.',
      'Mercury boils at about 357 °C, while gold does not even melt until about 1,064 °C. So heating the amalgam separates them: the mercury escapes as a gas and the gold stays put.',
    ],
    realLife: [
      'Small-scale gold miners in many countries still mix mercury with crushed rock or river sand to collect tiny specks of gold, then burn off the mercury. Today this is the biggest source of mercury pollution in the world. Mercury in rivers builds up in fish and can harm people who eat them.',
      "In 2013 countries signed the Minamata Convention, an agreement to reduce the use of mercury and protect people's health.",
    ],
    history: [
      'For centuries, metalworkers used gold amalgam to gild statues, domes and jewellery. They painted the paste onto bronze or copper and heated it, so the mercury boiled off and left a thin, shining layer of gold. This was called fire gilding, and it was very dangerous for the workers who breathed the vapour.',
    ],
    tryAtHome: [
      "Don't try this at home. Mercury and its vapour are poisonous.",
      'You can try panning instead. In a pie dish, mix sand, small pebbles and a few small metal nuts or washers. Add water, then gently swirl and tip the dish so the water carries the light sand over the edge. The heavy metal stays behind, just as gold does when prospectors pan in rivers.',
    ],
    quiz: [
      {
        question: 'What is an amalgam?',
        answer: 'A metal dissolved in mercury.',
      },
      {
        question: 'How is the gold separated from the mercury?',
        answer: 'The amalgam is heated, the mercury boils away and the gold is left.',
      },
      {
        question: 'Why is using mercury in gold mining a problem?',
        answer: 'The mercury pollutes rivers and the air and harms people and wildlife.',
      },
    ],
  },
  {
    pair: ['Al', 'Hg'],
    see: [
      'When a scratched piece of aluminium is touched with mercury, nothing seems to happen at first. Then, within minutes, white hair-like strands start growing out of the metal, like fur or mould. They curl and lengthen as you watch, and the aluminium gets warm.',
      'If you brush the fluffy white strands away, more grow back in their place.',
      'The aluminium itself slowly gets eaten away underneath. A thin sheet of aluminium foil touched with mercury can end up full of holes and covered in white powder.',
    ],
    why: [
      "Aluminium is a very reactive metal, but it is normally protected by a thin, tough skin of aluminium oxide. Mercury gets under the skin at a scratch and dissolves some aluminium, forming an amalgam. Now the aluminium's surface is liquid and cannot hold a protective skin any more.",
      'Oxygen and water vapour from the air attack the exposed aluminium straight away, turning it into aluminium oxide and hydroxide. Normally this would stop as soon as a thin layer formed. Here, the amalgam keeps lifting the oxide away and exposing fresh metal, so the white oxide grows outwards in long fibres. The reaction gives out heat, which is why the metal warms up.',
    ],
    realLife: [
      'This dramatic effect is the reason mercury is banned from aeroplanes. Many aircraft parts are made of aluminium alloys, and even a small mercury spill could weaken them.',
      'Chemists sometimes use aluminium amalgam on purpose, because the freshly exposed aluminium is a powerful helper for certain reactions in the lab.',
      'The experiment also shows just how reactive aluminium really is. Without its invisible oxide skin, a drink can would slowly crumble away in ordinary air.',
    ],
    tryAtHome: [
      "Don't try this at home. Mercury is poisonous and should never be touched.",
      'You can grow fluffy, needle-like crystals safely instead. With a grown-up, stir Epsom salt into a little hot water until no more will dissolve. Add a drop of washing-up liquid, then paint the mixture onto a sheet of black paper. As it dries over an hour or so, feathery white needles of crystal grow across the paper. Wash your hands afterwards.',
    ],
    quiz: [
      {
        question: 'What normally protects aluminium from air?',
        answer: 'A thin, tough layer of aluminium oxide.',
      },
      {
        question: 'What are the white hair-like strands made of?',
        answer: 'Aluminium oxide and hydroxide, formed by air attacking the exposed metal.',
      },
      {
        question: 'Why is mercury not allowed on aeroplanes?',
        answer: 'It can attack and weaken the aluminium alloys that many aircraft parts are made of.',
      },
    ],
  },
  {
    pair: ['Fe', 'Hg'],
    see: [
      'Drop an iron nail into a dish of mercury and something odd happens: it floats. Mercury is so dense that iron, which sinks in water, bobs on its surface. Leave it there for days and nothing else happens. The mercury does not coat the iron, and the iron does not dissolve.',
      'Lift the nail out and it comes out clean and dry, with no mercury clinging to it.',
      'Copper or brass dipped in the same dish would come out with a shiny silver coating, because mercury does dissolve into those metals.',
    ],
    why: [
      'Mercury dissolves many metals, such as gold, silver, tin and zinc, to make amalgams. But iron is one of the few common metals that mercury will not dissolve. Iron atoms bond very strongly to each other, and mercury atoms are not attracted to iron enough to pull them apart.',
      'Because the mercury is not attracted to the iron, it does not wet it. Instead, the mercury pulls itself into rounded droplets that roll off, just as water rolls off a waxy leaf. Floating is a separate effect: mercury weighs about 13.5 times as much as the same amount of water, nearly twice as dense as iron.',
    ],
    realLife: [
      'Because iron and mercury ignore each other, mercury has long been stored and shipped in iron or steel flasks. A standard flask holds 76 pounds of mercury, about 34.5 kilograms, and mercury is still bought and sold by the flask.',
      'Steel is also used for equipment in factories that handle mercury, because the mercury will not eat into it.',
    ],
    history: [
      'The traditional iron mercury flask became so standard that the flask itself turned into a unit for weighing and pricing mercury. Mercury prices are still often quoted per flask today.',
    ],
    tryAtHome: [
      "Don't touch mercury. If an old thermometer breaks, leave the room and tell a grown-up.",
      'You can explore floating and density safely. With a grown-up, pour honey, then washing-up liquid, then water with food colouring, then cooking oil slowly into a tall glass. They form layers. Gently drop in small objects such as a grape, a coin, a bead and a piece of pasta, and see which layer each one floats on.',
    ],
    quiz: [
      {
        question: 'Does iron sink or float in mercury?',
        answer: 'It floats, because mercury is much denser than iron.',
      },
      {
        question: 'Why is mercury stored in iron flasks?',
        answer: 'Mercury does not dissolve or attack iron.',
      },
      {
        question: 'How much mercury is in a standard flask?',
        answer: '76 pounds, about 34.5 kilograms.',
      },
    ],
  },
  {
    pair: ['H', 'O'],
    see: [
      'A balloon filled with hydrogen alone just floats. But a balloon filled with hydrogen and oxygen together is a dangerous mix. When a teacher touches it with a flame on the end of a long pole, there is a blinding flash and an ear-splitting bang.',
      'Afterwards there is no smoke and no soot. The only thing made is water, which is so hot that it flies off as steam.',
    ],
    why: [
      'Hydrogen gas is made of pairs of hydrogen atoms, and oxygen gas of pairs of oxygen atoms. To make water, these pairs must break apart, and then two hydrogen atoms join each oxygen atom. A spark or flame gives the first molecules enough energy to break apart.',
      'The new bonds in water are much stronger than the old ones, so making them releases a lot of energy. That energy breaks up more molecules nearby, which react and release even more energy. With the gases already mixed, the whole balloon reacts in a fraction of a second, and the sudden expansion of hot gas makes the bang.',
    ],
    realLife: [
      "Hydrogen and oxygen make a powerful rocket fuel. The Space Shuttle's main engines burned liquid hydrogen with liquid oxygen, and their exhaust was mostly steam.",
      'Fuel cells join hydrogen and oxygen quietly, without a flame, to make electricity and water. Some buses and cars run on them. The Apollo spacecraft used fuel cells, and astronauts drank the water they made.',
    ],
    history: [
      'In 1781 the English scientist Henry Cavendish showed that burning hydrogen in air made water. The French chemist Antoine Lavoisier then gave hydrogen its name, from Greek words meaning water former. In 1937 the airship Hindenburg, filled with hydrogen, caught fire while landing in the USA, and the age of hydrogen passenger airships came to an end.',
    ],
    tryAtHome: [
      "Don't try this at home. Hydrogen and oxygen mixtures explode.",
      'You can do the reverse safely: split water into its gases. With a grown-up, stir a teaspoon of baking soda into a glass of water. Take a 9-volt battery and attach two wires to its ends, each with a pencil lead or a sharpened pencil on the other end. Dip the leads into the water without letting them touch. Tiny bubbles appear: hydrogen on one, oxygen on the other.',
    ],
    quiz: [
      {
        question: 'What is the only product when hydrogen burns in oxygen?',
        answer: 'Water.',
      },
      {
        question: 'What does the name hydrogen mean?',
        answer: 'Water former.',
      },
      {
        question: 'What do hydrogen fuel cells make?',
        answer: 'Electricity and water.',
      },
    ],
  },
  {
    pair: ['F', 'H'],
    see: [
      'When hydrogen gas meets fluorine gas, there is no waiting for a spark. They explode the instant they mix, even in complete darkness and even when cooled far below freezing. The product is hydrogen fluoride, a colourless gas with a sharp, choking smell.',
      'This is one of the most violent reactions between two gases known.',
    ],
    why: [
      'Fluorine is the most electron-hungry element in the whole periodic table. Fluorine gas is made of pairs of atoms held together by a weak bond, so it does not take much to split them.',
      'Hydrogen and fluorine then form one of the strongest single bonds in chemistry, releasing a huge amount of energy. Each reaction releases enough energy to start more. Because fluorine pairs break apart so easily, the reaction does not need a flame, light or heat to start. Hydrogen and chlorine, by contrast, usually need light, and hydrogen and oxygen need a spark.',
    ],
    realLife: [
      'Hydrogen fluoride dissolved in water is called hydrofluoric acid. It is one of the few chemicals that can eat into glass. It is used to frost glass for decorations and light bulbs, and to etch the tiny patterns on computer chips.',
      'It is also used to make fluoride compounds. The fluoride in toothpaste, which strengthens tooth enamel, is a much safer compound, usually sodium fluoride.',
      'Fluorine compounds made from hydrogen fluoride also give us non-stick coatings for frying pans and some of the gases used in fridges and air conditioners.',
    ],
    history: [
      'Fluorine was so dangerous that many chemists were injured trying to separate it. In 1886 the French chemist Henri Moissan finally managed it, using electricity and equipment cooled to very low temperatures. He won the Nobel Prize in Chemistry in 1906.',
    ],
    tryAtHome: [
      "Don't try this at home. Fluorine and hydrogen fluoride are extremely dangerous.",
      'You can see how fluoride toothpaste protects teeth. With a grown-up, take a hard-boiled egg and cover half of the shell with fluoride toothpaste. Leave it for an hour, rinse it gently, then put the egg in a glass of vinegar overnight. The shell, like your tooth enamel, is made of a calcium mineral that acid can attack. Which half bubbles and softens more?',
    ],
    quiz: [
      {
        question: 'Do hydrogen and fluorine need a spark to react?',
        answer: 'No, they explode as soon as they mix, even in the dark.',
      },
      {
        question: 'What can hydrofluoric acid eat into that most acids cannot?',
        answer: 'Glass.',
      },
      {
        question: 'Who first separated fluorine, in 1886?',
        answer: 'Henri Moissan.',
      },
    ],
  },
  {
    pair: ['Cl', 'H'],
    see: [
      'Fill a clear container with a mixture of colourless hydrogen and pale green chlorine and keep it in the dark. Very little happens. Put it in dim light and the green colour slowly fades as the gases react. But shine a bright light or let direct sunlight hit it, and the mixture can explode with a loud crack.',
      'The product, hydrogen chloride, is a colourless gas that makes white fumes in damp air.',
    ],
    why: [
      'Chlorine gas is made of pairs of chlorine atoms. Light carries energy, and a strong enough bit of light can split a chlorine pair into two single, very reactive atoms.',
      'A single chlorine atom grabs a hydrogen atom from a hydrogen molecule, leaving a single hydrogen atom. That hydrogen atom then grabs a chlorine atom from another chlorine molecule, leaving another single chlorine atom. Each step makes hydrogen chloride and a new reactive atom, so one flash of light can set off thousands of reactions in a row. Chemists call this a chain reaction. In bright light, many chains start at once and the whole mixture reacts in an instant.',
    ],
    realLife: [
      'When hydrogen chloride dissolves in water it makes hydrochloric acid. Your stomach makes a weak version of it to help digest food and kill germs.',
      'Factories make pure hydrogen chloride by burning hydrogen in chlorine in a carefully controlled flame. The acid is used to clean rust off steel, make PVC plastic, and process food and water.',
    ],
    history: [
      'In the 1850s Robert Bunsen and Henry Roscoe used mixtures of hydrogen and chlorine to measure the strength of light. How fast the gases reacted told them how bright the light was. Their work helped start photochemistry, the study of reactions driven by light.',
    ],
    tryAtHome: [
      "Don't try this at home. Chlorine and hydrogen chloride are poisonous gases.",
      'You can see light drive a change safely. Place a few coins, keys or leaves on a sheet of dark coloured paper and put it on a sunny windowsill. Leave it for several days, then remove the objects. Light breaks down the dye in the paper, so the uncovered parts fade while the covered shapes stay dark.',
    ],
    quiz: [
      {
        question: 'What starts the reaction between hydrogen and chlorine?',
        answer: 'Light, especially bright light or sunlight.',
      },
      {
        question: 'What is a chain reaction?',
        answer: 'A reaction where each step makes a new reactive particle that starts the next step.',
      },
      {
        question: 'What do you get when hydrogen chloride dissolves in water?',
        answer: 'Hydrochloric acid.',
      },
    ],
  },
  {
    pair: ['H', 'N'],
    see: [
      'Mix nitrogen and hydrogen gas in a jar and nothing happens, even for years. To make them react, factories squeeze the gases to around 200 times normal air pressure, heat them to about 450 °C and pass them over lumps of iron. Slowly, a little ammonia forms.',
      'Ammonia is a colourless gas with a very strong, sharp smell that makes your eyes water.',
    ],
    why: [
      'Nitrogen atoms in nitrogen gas are joined in pairs by an extremely strong triple bond. Breaking it is very hard, which is why nitrogen is so unreactive.',
      "The iron acts as a catalyst, a helper that speeds the reaction without being used up. Nitrogen and hydrogen molecules stick to the iron's surface, where their bonds are weakened and pulled apart. Then each nitrogen atom joins with three hydrogen atoms to make ammonia. High pressure pushes the gases together and helps more ammonia form. The temperature is a compromise: hotter makes the reaction faster, but too hot makes the ammonia break apart again.",
    ],
    realLife: [
      "Plants need nitrogen to grow, but they cannot use nitrogen gas from the air. Most ammonia made in factories is turned into fertiliser, and scientists estimate that about half the world's food is grown with its help.",
      'Ammonia is also used to make cleaning products, plastics, fibres for clothes and some medicines.',
    ],
    history: [
      'In 1909 the German chemist Fritz Haber found a way to make ammonia from nitrogen and hydrogen in the lab. The engineer Carl Bosch scaled it up, and the first factory opened in 1913. It is called the Haber-Bosch process, and both men won Nobel Prizes.',
    ],
    tryAtHome: [
      "Don't sniff ammonia or mix cleaning products. Some cleaners contain ammonia, and mixing them with bleach makes a poisonous gas.",
      'You can test fertiliser safely. With a grown-up, plant cress seeds on damp cotton wool in two pots. Water one with plain water and the other with water containing a few drops of plant food. Keep them on the same windowsill and measure them each day. Keep plant food away from your mouth and wash your hands.',
    ],
    quiz: [
      {
        question: 'What does the iron do in ammonia making?',
        answer: 'It acts as a catalyst, speeding up the reaction without being used up.',
      },
      {
        question: 'What is most factory-made ammonia used for?',
        answer: 'Fertiliser to help crops grow.',
      },
      {
        question: 'What is the process for making ammonia called?',
        answer: 'The Haber-Bosch process.',
      },
    ],
  },
  {
    pair: ['C', 'O'],
    see: [
      'Charcoal is almost pure carbon. Once a barbecue is lit, the black lumps glow orange and red, giving off steady heat with hardly any flame. Over an hour or two they shrink and turn into a little pile of pale grey ash.',
      'Most of the charcoal has not vanished. It has turned into an invisible gas, carbon dioxide, which drifts away into the air.',
    ],
    why: [
      'Each carbon atom joins with two oxygen atoms from the air to make one molecule of carbon dioxide. Making these strong new bonds releases energy as heat and light. The ash left behind is made of minerals that were mixed in with the carbon and cannot burn.',
      'Charcoal glows rather than flames because the carbon is solid and burns right on its surface, where oxygen reaches it. If there is not enough oxygen, carbon can make carbon monoxide instead, a poisonous gas with no smell. That is why barbecues and fires must never be used indoors or in tents.',
      'Burning is a fast version of a slow reaction going on inside you. Your body also combines carbon from food with oxygen you breathe, releasing energy gently to keep you warm and moving.',
    ],
    realLife: [
      'Burning coal, wood and charcoal has warmed homes and cooked food for thousands of years. Coal-fired power stations burn carbon to make electricity, but the carbon dioxide they release adds to climate change.',
      'Carbon dioxide is also part of life. You breathe it out, plants take it in to make food using sunlight, and it is the gas that puts the fizz in fizzy drinks.',
    ],
    history: [
      'In 1772 the French chemist Antoine Lavoisier used a giant magnifying glass to focus sunlight onto a diamond inside a sealed jar. The diamond burned away and made carbon dioxide, proving that diamond is made of carbon, just like charcoal.',
    ],
    tryAtHome: [
      'Fire is dangerous, so do this only with a grown-up. Stand a tea light in a dish and let the grown-up light it. Carefully lower a large glass jar upside down over it and time how long the flame lasts. Try again with a smaller jar. The flame goes out when it has used up the oxygen it needs. Let everything cool before touching it.',
    ],
    quiz: [
      {
        question: 'What gas is made when charcoal burns in plenty of air?',
        answer: 'Carbon dioxide.',
      },
      {
        question: 'Why should barbecues never be used indoors?',
        answer: 'Burning with too little oxygen makes carbon monoxide, a poisonous gas with no smell.',
      },
      {
        question: 'What did Lavoisier prove by burning a diamond?',
        answer: 'That diamond is made of carbon.',
      },
    ],
  },
  {
    pair: ['N', 'O'],
    see: [
      'You cannot watch this reaction in a jar, because nitrogen and oxygen sit side by side in the air all day without reacting. It only happens where it is incredibly hot. Inside a lightning bolt, the air heats up to thousands of degrees, hotter than the surface of the Sun, and some nitrogen and oxygen join to make a colourless gas called nitric oxide.',
      'Once it cools in the air, nitric oxide picks up more oxygen and turns into nitrogen dioxide, a reddish-brown gas.',
    ],
    why: [
      'Nitrogen atoms in the air are paired up with an extremely strong triple bond, and oxygen atoms are paired too. To react, both pairs must be broken apart. At normal temperatures there is nowhere near enough energy for that, which is lucky for us, because the air would otherwise slowly change.',
      "The enormous heat of lightning or of a car engine's burning fuel shakes some molecules apart. The loose nitrogen and oxygen atoms then join up as nitric oxide, one nitrogen with one oxygen. This reaction actually takes in energy rather than giving it out, so it only keeps going while things stay extremely hot.",
    ],
    realLife: [
      'Car engines and power stations make nitrogen oxides, which add to smog and acid rain. Catalytic converters in car exhausts use metals such as platinum and rhodium to turn most of them back into harmless nitrogen.',
      'Nitric oxide also works inside your body, in tiny amounts. It is a messenger that tells blood vessels to relax and widen. Scientists who discovered this won a Nobel Prize in 1998.',
    ],
    history: [
      "In 1903 Kristian Birkeland and Sam Eyde in Norway built giant electric sparks, a kind of artificial lightning, to join nitrogen and oxygen from the air. They used the result to make fertiliser, powered by Norway's cheap hydroelectric power.",
    ],
    tryAtHome: [
      'Stay indoors during thunderstorms. From a safe window, you can measure how far away lightning is. When you see a flash, count the seconds until you hear the thunder. Divide by three to get the distance in kilometres, or by five for miles. Light reaches you almost instantly, but sound travels much more slowly.',
    ],
    quiz: [
      {
        question: "Why don't nitrogen and oxygen in the air normally react?",
        answer: "Their atoms are held in strong pairs, and normal temperatures don't give enough energy to break them.",
      },
      {
        question: 'Name two things that can make nitrogen and oxygen react.',
        answer: 'Lightning and hot car engines.',
      },
      {
        question: 'What does nitric oxide do inside your body?',
        answer: 'It acts as a messenger that helps blood vessels relax and widen.',
      },
    ],
  },
  {
    pair: ['O', 'S'],
    see: [
      'Yellow sulfur melts into a red-brown liquid when heated. When it catches fire, it burns with a small, beautiful blue flame that is easy to miss in daylight but glows clearly in a dark room.',
      'As it burns, it gives off sulfur dioxide, a colourless gas with a sharp, choking smell, like a match that has just been struck.',
    ],
    why: [
      'Each sulfur atom joins with two oxygen atoms to make one molecule of sulfur dioxide. Forming those bonds releases energy as heat and light.',
      'The blue colour comes from the hot molecules themselves. When atoms and molecules are excited by heat, they give out light of particular colours as they calm down. Molecules formed in a sulfur flame give out mostly blue light. Sulfur dioxide dissolves in water to make a weak acid, which is why it stings the nose, throat and eyes.',
      'In the air, sulfur dioxide slowly reacts with more oxygen and with rain water to make sulfuric acid. This is one of the main causes of acid rain, which can damage forests, lakes and old stone buildings. Power stations now scrub most sulfur dioxide out of their smoke before it escapes.',
    ],
    realLife: [
      'Volcanoes release huge amounts of sulfur dioxide. At the Kawah Ijen volcano in Indonesia, sulfur gases catch fire at night and make rivers of electric-blue flames.',
      'Factories burn sulfur to make sulfur dioxide, then turn it into sulfuric acid, one of the most important chemicals in industry. In tiny, safe amounts, sulfur dioxide is also used to stop dried fruit such as apricots from going brown. You may see it listed as E220 on food labels.',
    ],
    history: [
      'In 1991 Mount Pinatubo in the Philippines erupted and blasted millions of tonnes of sulfur dioxide high into the sky. It formed a thin haze that reflected some sunlight away, and the whole Earth cooled by about half a degree Celsius for a year or two.',
    ],
    tryAtHome: [
      "Don't burn sulfur. Its gas harms your lungs.",
      'Instead, go on a label hunt with a grown-up. Look at the ingredients on packets of dried apricots, raisins or other dried fruit. Can you find sulphur dioxide or E220? Compare the colour of bright orange dried apricots with dark brown ones labelled unsulphured.',
    ],
    quiz: [
      {
        question: 'What colour flame does sulfur burn with?',
        answer: 'Blue.',
      },
      {
        question: 'What gas is made when sulfur burns?',
        answer: 'Sulfur dioxide.',
      },
      {
        question: 'Why does sulfur dioxide sting your nose and throat?',
        answer: 'It dissolves in water to make a weak acid.',
      },
    ],
  },
  {
    pair: ['O', 'P'],
    see: [
      'White phosphorus is a waxy, pale yellow solid. In a dark room, a piece of it exposed to air gives off a faint, ghostly green glow. If it warms up just a little above room temperature, it bursts into flame all by itself, making thick clouds of white smoke.',
      'The smoke is phosphorus pentoxide, which settles as a white powder that greedily soaks up water.',
    ],
    why: [
      'White phosphorus is made of tiny groups of four phosphorus atoms arranged in a strained triangular pyramid. The bonds in this shape are bent and under strain, so they break easily and phosphorus reacts with oxygen very readily.',
      'Even at room temperature, phosphorus vapour reacts slowly with oxygen just above the surface. Some of the energy released comes out as light instead of heat, which makes the green glow. This is called chemiluminescence. When the phosphorus gets warm enough, the reaction runs away and it ignites. Red phosphorus, made of the same atoms arranged differently, is much safer and does not glow or catch fire on its own.',
    ],
    realLife: [
      'The striking strip on a box of safety matches contains red phosphorus. Rubbing the match head across it makes enough heat to light the match.',
      'Phosphorus is essential for life. It is in your bones, your teeth and your DNA. Phosphorus pentoxide reacts with water to make phosphoric acid, which is used in fertilisers and gives some cola drinks their sharp taste.',
    ],
    history: [
      'Phosphorus was discovered in 1669 by Hennig Brand, a German alchemist trying to make gold. He boiled down huge amounts of urine and heated the remains strongly, and got a substance that glowed in the dark. He named it phosphorus, Greek for light bringer.',
    ],
    tryAtHome: [
      "Don't try this at home. White phosphorus is poisonous and catches fire by itself.",
      'Glow sticks use chemiluminescence too. Bend a glow stick to start it, then put one in a glass of warm water and another in a glass of ice water. Which glows brighter? Which lasts longer? Never cut a glow stick open.',
    ],
    quiz: [
      {
        question: 'Why does white phosphorus glow in the dark?',
        answer: 'It reacts slowly with oxygen, and some of the energy comes out as light.',
      },
      {
        question: 'Which kind of phosphorus is on a matchbox striking strip?',
        answer: 'Red phosphorus, which is much safer than white.',
      },
      {
        question: 'What did Hennig Brand use to make phosphorus?',
        answer: 'Urine.',
      },
    ],
  },
  {
    pair: ['F', 'S'],
    see: [
      'Sulfur catches fire in fluorine gas without any spark or match. It burns, and the product is sulfur hexafluoride, a colourless gas with no smell.',
      'The new gas is about five times heavier than air. In a famous demonstration, it is poured into a fish tank, where it sits invisibly at the bottom, and a little foil boat placed on top floats on nothing you can see.',
    ],
    why: [
      'Fluorine is the most electron-hungry element. It is so strong that it can make sulfur share six of its electrons, so each sulfur atom ends up surrounded by six fluorine atoms.',
      'These six fluorine atoms are arranged evenly around the sulfur, like the corners of two pyramids stuck base to base. Together they form a tight, balanced shell that protects the sulfur in the middle. That is why sulfur hexafluoride, made by such a fierce reaction, is itself extremely calm and unreactive. It is heavy because each molecule contains seven atoms, six of them fairly heavy fluorine atoms.',
      "Breathing in a little sulfur hexafluoride makes a person's voice sound much deeper for a moment, the opposite of helium. It is still not a safe thing to play with, because any heavy gas can push the oxygen out of your lungs.",
    ],
    realLife: [
      'Sulfur hexafluoride is excellent at stopping electric sparks, so it is used inside high-voltage electrical equipment at power stations.',
      'But it has a big downside. It is an extremely powerful greenhouse gas, trapping heat thousands of times more strongly than carbon dioxide, and it stays in the air for thousands of years. Engineers are working to replace it.',
    ],
    history: [
      'Sulfur hexafluoride was first made in 1901 by the French chemists Henri Moissan and Paul Lebeau. Moissan had been the first person to separate fluorine itself, fifteen years earlier.',
    ],
    tryAtHome: [
      "Don't try this at home. Fluorine is extremely dangerous, and sulfur burning in it is a job for specialist labs only.",
      'Heavy gas makes voices sound deeper because sound moves differently through it. You can explore how sound travels through materials safely. Make a string telephone with two paper cups and a long piece of string pulled tight. Talk into one cup while a friend listens. Then let the string go slack. What happens?',
    ],
    quiz: [
      {
        question: 'How many fluorine atoms surround each sulfur atom in sulfur hexafluoride?',
        answer: 'Six.',
      },
      {
        question: 'Why can a foil boat float on sulfur hexafluoride?',
        answer: 'The gas is about five times heavier than air, so it sits in the tank and holds the boat up.',
      },
      {
        question: 'Why are engineers trying to replace sulfur hexafluoride?',
        answer: 'It is a very powerful and long-lasting greenhouse gas.',
      },
    ],
  },
  {
    pair: ['C', 'Si'],
    see: [
      'Silicon carbide is made in huge electric furnaces. Sand, which contains silicon, and coke, which is mostly carbon, are heated around a glowing carbon rod to over 2,000 °C. After many hours the furnace is cooled and broken open.',
      'Inside are sparkling, dark crystals that can shine green, blue or black with rainbow colours on their surface.',
    ],
    why: [
      'Silicon and carbon sit in the same column of the periodic table, so both atoms like to make four bonds. In silicon carbide, every silicon atom is bonded to four carbon atoms, and every carbon atom to four silicon atoms, in a giant 3D framework.',
      'This is the same kind of strong, interlocking network that makes diamond so hard. Every atom is held firmly in place in all directions, so the crystal is extremely hard to scratch and only melts or breaks down at very high temperatures. The furnace has to be so hot because the silicon first has to be pulled out of the sand and its oxygen removed by the carbon.',
    ],
    realLife: [
      'Silicon carbide is one of the hardest materials people make. It is used as the gritty coating on some sandpapers and grinding wheels, and in cutting tools.',
      'It is also used in brake discs for fast cars, in body armour, and in electronic chips that control power in electric cars and solar panels, because it can handle high temperatures and high voltages.',
    ],
    history: [
      'In 1891 the American inventor Edward Goodrich Acheson was trying to make artificial diamonds. Instead he found shiny blue crystals that were almost as hard, and named them carborundum. A few years later, Henri Moissan found tiny natural crystals of silicon carbide in a meteorite. Natural silicon carbide is now called moissanite in his honour.',
    ],
    tryAtHome: [
      'With a grown-up, compare sandpapers of different grit. Rub a coarse sheet and a fine sheet over two parts of a scrap of wood or a bar of soap. Look with a magnifying glass. Coarse grit has bigger, harder particles that dig deeper scratches. Wear a mask if it gets dusty, and wash your hands.',
    ],
    quiz: [
      {
        question: 'How many bonds does each silicon atom make in silicon carbide?',
        answer: 'Four, each to a carbon atom.',
      },
      {
        question: 'What was Edward Acheson trying to make when he discovered carborundum?',
        answer: 'Artificial diamonds.',
      },
      {
        question: 'Name one use of silicon carbide.',
        answer: 'Sandpaper, grinding wheels, brake discs, body armour or power chips in electric cars.',
      },
    ],
  },
  {
    pair: ['F', 'Xe'],
    see: [
      'Xenon is a colourless noble gas. When xenon and fluorine gases are mixed in a sealed container and warmed or exposed to sunlight, colourless crystals slowly grow on the inside of the container.',
      'These crystals are xenon difluoride, a compound that scientists once believed could never exist.',
    ],
    why: [
      'Noble gases have full outer layers of electrons, which makes them very stable. For a long time, chemists thought they could not react at all.',
      "Xenon is a big atom, and its outer electrons are far from its centre, so they are held a little less tightly than in smaller noble gases like neon or helium. Fluorine is the most electron-hungry element. When light or heat breaks fluorine molecules into single atoms, those atoms are strong enough to pull on xenon's outer electrons and make two bonds, one on each side. Even so, only the strongest electron-grabbers, such as fluorine and oxygen, can do this.",
      'Smaller noble gases are even harder to tempt. Krypton forms a fluoride only with great difficulty, and no stable compounds of helium or neon have ever been made.',
    ],
    realLife: [
      'Xenon difluoride is used in making computer chips, because it etches silicon very precisely. Chemists also use it to add fluorine atoms to other molecules.',
      "Xenon itself makes bright camera flashes and some car headlights, and it powers ion engines that push spacecraft gently but steadily for years. NASA's Dawn spacecraft used xenon ion engines to visit the dwarf planet Ceres.",
    ],
    history: [
      'Xenon was discovered in 1898 by William Ramsay and Morris Travers, who named it after the Greek word for stranger. In 1962 the chemist Neil Bartlett made the first noble gas compound, from xenon. Within months, other teams had made xenon fluorides, and chemistry textbooks had to be rewritten.',
    ],
    tryAtHome: [
      "Don't try this at home. Fluorine is extremely dangerous, and xenon compounds are made only in specialist labs.",
      "You can build a model of why noble gases are so calm. Draw circles on a paper plate for an atom's electron layers and use sweets as electrons. Two fit in the first layer, eight in the next. Build neon with 10 sweets, and see how both layers are full. Then build sodium with 11 and notice the lonely extra one.",
    ],
    quiz: [
      {
        question: 'Why did scientists think noble gases could never react?',
        answer: 'Their outer layers of electrons are full, which makes them very stable.',
      },
      {
        question: 'Which element can pull electrons from xenon?',
        answer: 'Fluorine.',
      },
      {
        question: 'What year was the first noble gas compound made?',
        answer: '1962.',
      },
    ],
  },
  {
    pair: ['F', 'H2O'],
    see: [
      'Bubble fluorine gas into water and the water does not simply dissolve it, as it would with many gases. It reacts fiercely, sometimes with a flame at the surface. The water turns into a strong acid, hydrofluoric acid, and oxygen gas bubbles out.',
      'Small amounts of other gases, including ozone, can also form, giving a sharp smell.',
    ],
    why: [
      'Water is a very stable molecule. Each oxygen atom holds onto its two hydrogen atoms tightly. Almost nothing can snatch them away.',
      "Fluorine is the exception. It wants electrons even more than oxygen does. When fluorine meets water, it pulls the hydrogen atoms away from the oxygen to make hydrogen fluoride, and the oxygen atoms left behind pair up and escape as oxygen gas. In this reaction, fluorine takes oxygen's place, which shows that fluorine is the most reactive non-metal of all.",
      'Very few substances can do this. Even chlorine, the next halogen down, only partly reacts with water and leaves most of it unchanged.',
    ],
    realLife: [
      'Fluorine itself is far too dangerous for everyday use, but its compounds are everywhere. In many places, a tiny amount of fluoride is added to drinking water to help prevent tooth decay, and fluoride is in most toothpastes.',
      'The non-stick coating on many frying pans is a plastic made from fluorine and carbon. Water rolls off it in beads because the surface barely attracts water at all.',
    ],
    history: [
      'The mineral fluorite, made of calcium and fluorine, was used by metal workers in the 1500s to help melted metal flow, and the name fluorine comes from the Latin word fluere, to flow. In 1938 the chemist Roy Plunkett discovered the non-stick plastic PTFE by accident when a cylinder of gas he expected to use turned into a slippery white powder.',
    ],
    tryAtHome: [
      "Don't try this at home. Fluorine is extremely dangerous, and the acid it makes from water burns badly.",
      'With a grown-up, compare two cold, clean pans: one non-stick and one stainless steel. Drip a few drops of water onto each. On the non-stick pan the water forms round beads that roll around. On the steel pan it spreads out more. The fluorine-made coating is the reason for the difference.',
    ],
    quiz: [
      {
        question: 'What gas bubbles out when fluorine reacts with water?',
        answer: 'Oxygen.',
      },
      {
        question: 'Why can fluorine pull hydrogen away from oxygen in water?',
        answer: 'Fluorine wants electrons even more than oxygen does.',
      },
      {
        question: 'What does the name fluorine come from?',
        answer: 'The Latin word fluere, meaning to flow.',
      },
    ],
  },
  {
    pair: ['Cl', 'H2O'],
    see: [
      'When pale green chlorine gas is bubbled into water, the water turns a very pale yellow-green and smells strongly, a bit like a swimming pool. Nothing explodes or bubbles. The change is quiet, but the water has become a germ-killer.',
      'Left in sunlight, chlorine water slowly loses its colour and smell as tiny bubbles of oxygen form.',
    ],
    why: [
      'Some of the chlorine just dissolves, but some of it reacts with the water. Each chlorine molecule splits: one chlorine atom joins a hydrogen to make hydrochloric acid, and the other joins with oxygen and hydrogen to make hypochlorous acid.',
      'Only part of the chlorine reacts, and the reaction can run backwards as well as forwards, so the water ends up with a balance of chlorine, both acids and the ions they make. Hypochlorous acid is the hero. It slips into bacteria and viruses and damages the molecules they need to live. Sunlight breaks hypochlorous acid down, releasing oxygen, which is why outdoor pools need chlorine topped up.',
    ],
    realLife: [
      'Adding small, carefully measured amounts of chlorine to drinking water kills the germs that cause diseases such as cholera and typhoid. It has saved millions of lives.',
      'Swimming pools use chlorine too. The strong pool smell is not really chlorine. It comes from chloramines, made when chlorine reacts with sweat and wee in the water. A very smelly pool usually needs more cleaning, not less chlorine.',
      'Many cleaning products, such as household bleach, contain a close relative of hypochlorous acid that kills germs in the same way.',
    ],
    history: [
      'In 1908, Jersey City in the United States became one of the first cities to add chlorine to its public drinking water continuously. Deaths from waterborne diseases dropped sharply, and many other cities soon followed.',
    ],
    tryAtHome: [
      'Never mix bleach or pool chemicals with anything. Some mixtures release poisonous chlorine gas.',
      'Clean water and clean hands both stop germs. Rub a little cooking oil on your hands, then sprinkle a pinch of glitter on them. Try washing it off with cold water only, then with warm water and soap for 20 seconds. The glitter shows how germs stick to skin and how soap helps lift them away.',
    ],
    quiz: [
      {
        question: 'Which acid made from chlorine and water kills germs?',
        answer: 'Hypochlorous acid.',
      },
      {
        question: 'Why do outdoor pools need chlorine topped up?',
        answer: 'Sunlight breaks down the hypochlorous acid.',
      },
      {
        question: 'What really causes the strong swimming-pool smell?',
        answer: 'Chloramines, made when chlorine reacts with sweat and wee.',
      },
    ],
  },
  {
    pair: ['Br', 'H2O'],
    see: [
      'Bromine is a dark red-brown liquid that gives off orange fumes. When a few drops are shaken with water, some of it dissolves and the water turns yellow-orange, depending on how much bromine is in it. This is called bromine water.',
      'Extra bromine that does not dissolve sinks to the bottom as heavy dark drops.',
    ],
    why: [
      'Bromine is a halogen, like chlorine, but its atoms are bigger and hold onto extra electrons a little less strongly. So bromine is less reactive with water than chlorine. Most of the bromine just dissolves, giving the orange colour, and only a small amount reacts to make hydrobromic acid and hypobromous acid.',
      'Bromine water is useful in chemistry tests. Some carbon compounds have a double bond between two carbon atoms. Bromine adds across that double bond and is used up, so the orange colour disappears and the water turns clear.',
      'Bromine water can also show which substances give away electrons. Adding it to a solution of potassium iodide turns the liquid a deeper brown, because bromine takes electrons from iodide and sets iodine free.',
    ],
    realLife: [
      'Chemists shake orange bromine water with an unknown oil or gas. If the colour vanishes, the substance contains carbon-carbon double bonds, like those in many vegetable oils.',
      "Bromine compounds are also used to keep hot tubs clean, and the Dead Sea is one of the world's richest sources of bromine because its water contains so much bromide salt.",
      'Some medicines contain bromine atoms, and in Victorian times bromide salts were given to people to help them calm down and sleep.',
    ],
    history: [
      'Long before bromine was discovered, people treasured a dye that contains it. Tyrian purple was made from sea snails in the ancient Mediterranean. It took thousands of snails to dye a single robe, so purple clothing was worn by emperors and kings. Modern chemists found that the dye molecule contains bromine atoms.',
    ],
    tryAtHome: [
      "Don't try this at home. Bromine is poisonous and burns skin.",
      'You can make your own colour-changing test liquid. With a grown-up, chop some red cabbage, cover it with hot water and let it cool, then strain the purple juice. Add a little to separate cups of vinegar, lemon juice, water and baking soda solution. Acids turn it pink and alkalis turn it green or blue.',
    ],
    quiz: [
      {
        question: 'What colour is bromine water?',
        answer: 'Yellow-orange.',
      },
      {
        question: 'What happens to bromine water when it meets a carbon-carbon double bond?',
        answer: 'The orange colour disappears and it turns clear.',
      },
      {
        question: 'Which ancient purple dye contains bromine?',
        answer: 'Tyrian purple, made from sea snails.',
      },
    ],
  },
  {
    pair: ['C', 'H2O'],
    see: [
      'In old gasworks, steam was blown through a bed of coke, a form of carbon, that had been heated until it glowed white hot, above about 1,000 °C. Out came a mixture of two invisible gases that burned with a pale blue flame.',
      'As the steam went through, the glowing coke cooled down and stopped glowing. Workers then had to blast air through it to heat it up again before the next round of steam.',
    ],
    why: [
      'At very high temperatures, carbon can steal oxygen atoms from water molecules. Each carbon atom takes one oxygen atom and becomes carbon monoxide. The two hydrogen atoms left behind pair up as hydrogen gas. The mixture of carbon monoxide and hydrogen is called water gas.',
      'This reaction takes in heat instead of giving it out, which is why the coke cools as the steam passes. Both gases in water gas can burn, so the mixture makes a useful fuel. But carbon monoxide is very poisonous, because it stops your blood from carrying oxygen, and it has no colour or smell.',
    ],
    realLife: [
      'Today, similar mixtures of carbon monoxide and hydrogen, called syngas, are made from coal, natural gas or waste. Factories use syngas to make hydrogen, methanol and other chemicals.',
      'Carbon monoxide is still a danger in homes, where it can come from faulty boilers, heaters or blocked chimneys. That is why homes should have carbon monoxide alarms.',
    ],
    history: [
      'In the 1800s and early 1900s, many towns made gas from coal to light streets and homes and to cook. In 1873 the American inventor Thaddeus Lowe improved the water gas process, and water gas was piped to homes in many cities until natural gas took its place.',
    ],
    tryAtHome: [
      "Don't try this at home. Carbon monoxide is deadly and you cannot see or smell it.",
      "Do a safety check instead. Ask a grown-up to show you your home's carbon monoxide alarm and smoke alarm. Help them press the test button, and check the date on the back. If you don't have a carbon monoxide alarm, talk to your family about getting one.",
    ],
    quiz: [
      {
        question: 'What two gases make up water gas?',
        answer: 'Carbon monoxide and hydrogen.',
      },
      {
        question: 'Why does the coke cool down when steam passes through?',
        answer: 'The reaction takes in heat instead of giving it out.',
      },
      {
        question: 'Why is carbon monoxide so dangerous?',
        answer: 'It is poisonous, stops your blood carrying oxygen, and has no colour or smell.',
      },
    ],
  },
  {
    pair: ['H2O', 'P'],
    see: [
      'Drop a stick of white phosphorus into a jar of water and nothing happens. It sinks and just sits there, quietly. It does not dissolve, fizz or change colour.',
      'Take the same stick out into the air and let it dry, and within minutes it can start to smoke and then burst into flames.',
    ],
    why: [
      'White phosphorus reacts eagerly with oxygen, but it does not react with water at all. It also hardly dissolves in water.',
      'So water acts like a protective blanket. It keeps oxygen from the air away from the phosphorus, and it keeps it cool, so it can never warm up enough to catch fire. That is why chemists store white phosphorus under water. This is quite unusual: most of the very reactive elements, such as sodium and potassium, have to be kept away from water and are stored under oil instead.',
    ],
    realLife: [
      'Today white phosphorus is mostly used by factories to make phosphoric acid and other phosphorus compounds for fertilisers, detergents and food. It is shipped and stored covered with water.',
      'If white phosphorus is ever found on a beach, for example from old wartime material washing ashore, it can look like a harmless stone or amber. It can catch fire once it dries, which is why you should never pick up strange yellowish lumps.',
    ],
    history: [
      'In the 1800s, white phosphorus was used in match heads. Factory workers, many of them young women and girls, breathed its fumes and some developed a terrible disease of the jaw called phossy jaw. In 1888 the match girls at the Bryant and May factory in London went on strike for better conditions. In the early 1900s many countries banned white phosphorus in matches.',
    ],
    tryAtHome: [
      "Don't try this with phosphorus. It is poisonous and catches fire by itself.",
      'You can test water as a protective blanket safely. Ask a grown-up to cut three slices of apple. Leave one on a plate, put one in a glass of water, and one in water with a squeeze of lemon. After an hour, which slice has turned brown? Oxygen in the air causes the browning.',
    ],
    quiz: [
      {
        question: 'Why is white phosphorus stored under water?',
        answer: 'Water keeps oxygen away and keeps it cool, so it cannot catch fire.',
      },
      {
        question: 'Does white phosphorus react with water?',
        answer: "No, it doesn't react with water at all.",
      },
      {
        question: 'What disease did match factory workers get from white phosphorus?',
        answer: 'Phossy jaw.',
      },
    ],
  },
  {
    pair: ['H2O', 'HCl'],
    see: [
      'When concentrated hydrochloric acid is poured slowly into a large amount of water, it mixes in smoothly. The liquid stays clear and colourless, but the container gets slightly warm.',
      'If it is done the wrong way round, adding a little water to a lot of strong acid, the heat can build up in one spot and make the liquid spit and splash.',
    ],
    why: [
      'Hydrochloric acid is already hydrogen chloride dissolved in water. Adding more water does not make a new substance. It just spreads the same acid particles out through more water, which is called diluting. The acid becomes weaker because there are fewer acid particles in each drop.',
      "Diluting gives out some heat, because water molecules gather tightly around the acid's ions. If you pour acid into lots of water, that heat is spread out and soaked up safely. If you pour water into strong acid, the first drops of water get very hot very quickly and can boil, splashing acid out. Chemists remember: do what you oughta, add the acid to the water.",
    ],
    realLife: [
      'Your stomach contains dilute hydrochloric acid, which helps break down food and kills many germs you swallow. A thick layer of mucus protects your stomach wall from the acid.',
      'Hydrochloric acid at different strengths is used to clean rust and scale off steel, to adjust the acidity of swimming pools, and in making many foods and medicines. How strongly acidic a liquid is can be measured on the pH scale, where lower numbers mean more acidic.',
    ],
    history: [
      'In the 1600s the German chemist Johann Rudolf Glauber made hydrochloric acid by heating salt with sulfuric acid, and it was called spirit of salt. In 1909 Søren Sørensen, a chemist at the Carlsberg Laboratory in Copenhagen, invented the pH scale to measure acidity.',
    ],
    tryAtHome: [
      "Don't use real acids at home. Acids burn skin and eyes.",
      'You can see dilution with food colouring. Line up five clear cups and fill four with water. Put strong food colouring water in the first cup. Pour half of it into the second cup and stir, then half of that into the third, and so on. Each cup is paler, but the colour is still there, just spread out.',
    ],
    quiz: [
      {
        question: 'Does mixing hydrochloric acid with water make a new substance?',
        answer: 'No, it just dilutes the acid, making it weaker.',
      },
      {
        question: 'Should you add acid to water or water to acid?',
        answer: 'Always add the acid to the water.',
      },
      {
        question: 'What scale measures how acidic something is?',
        answer: 'The pH scale.',
      },
    ],
  },
]);

const ARTICLES_BY_PAIR = new Map(REACTION_ARTICLES.map((article) => [createPairKey(...article.pair), article]));

export function findReactionArticle(reactantIds) {
  return ARTICLES_BY_PAIR.get(createPairKey(...reactantIds)) ?? null;
}
